import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import { z } from 'zod'
import { Brand, FacilityIdsQueryParamSchema, generateResultsWithOptionalPagination, INT32_MAX, PaginatedResultsQuerySchema } from '../../shared'
import { FeedingPlanType } from '../common'
import { BeCoolDepotCode } from '../courier'
import { DiscountInterval, PromotionType } from '../pricing'
import { ProductType } from '../product'
import { AddressState, DeliverySlotWindow, JobStatus, OrderComplaintResponsibilitySchema, OrderRescheduleReasonType, OrderSource, OrderType, PaymentStatus, RebalancingStatus } from './types'
import { isValidSearchOrdersQueryText } from './search-orders.util'

extendZodWithOpenApi(z)

export const UpdateAddressSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  suburb: z.string().optional(),
  city: z.string().optional(),
  state: z.nativeEnum(AddressState).optional(),
  postcode: z.string().optional(),
  countryCode: z.string().optional(),
  companyName: z.string().optional(),
  timezone: z.string().optional(),
})

export type UpdateAddressDto = z.infer<typeof UpdateAddressSchema>

export const AddressSchema = z.object({
  id: z.string().optional(),
  createdTimeUTC: z.coerce.date().optional(),
  updatedTimeUTC: z.coerce.date().optional(),
  firstName: z.string(),
  lastName: z.string(),
  address1: z.string(),
  address2: z.string().optional().nullable(),
  suburb: z.string(),
  city: z.string(),
  state: z.nativeEnum(AddressState),
  postcode: z.string(),
  countryCode: z.string(),
  companyName: z.string().optional().nullable(),
  timezone: z.string(),
})

export type AddressSchemaDto = z.infer<typeof AddressSchema>

export const AddressSchemaWithOrderId = AddressSchema.extend({
  orderId: z.string().uuid(),
})

export type AddressResponseDto = z.infer<typeof AddressSchemaWithOrderId>

export const OrderBundleLineItemsSchema = z.object({
  id: z.string().optional(),
  createdTimeUTC: z.coerce.date().optional(),
  updatedTimeUTC: z.coerce.date().optional(),
  animalId: z.string().uuid().optional().nullable(),
  extraId: z.string().uuid().optional().nullable(),
  productId: z.string().uuid().optional().nullable(),
  price: z.number().int().max(INT32_MAX).nonnegative(),
  quantity: z.number(),
})

export const OrderLineItemSchema = z.object({
  id: z.string().optional(),
  createdTimeUTC: z.coerce.date().optional(),
  updatedTimeUTC: z.coerce.date().optional(),
  bundleId: z.string().uuid().optional().nullable(),
  animalId: z.string().uuid().optional().nullable(),
  extraId: z.string().uuid().optional().nullable(),
  productId: z.string().uuid().optional().nullable(),
  productType: z.nativeEnum(ProductType).optional().nullable(),
  planType: z.nativeEnum(FeedingPlanType).optional().nullable(),
  price: z.number().int().max(INT32_MAX).nonnegative(),
  unitPrice: z.number().int().max(INT32_MAX).nonnegative().optional().nullable(),
  unitChargePrice: z.number().int().max(INT32_MAX).nonnegative().optional().nullable(),
  totalPrice: z.number().int().max(INT32_MAX).nonnegative().optional().nullable(),
  totalCharge: z.number().int().max(INT32_MAX).nonnegative().optional().nullable(),
  appliedDiscountId: z.string().uuid().optional().nullable(),
  appliedDiscountApplicationId: z.string().uuid().optional().nullable(),
  discountedQuantity: z.number().int().nonnegative().optional().nullable(),
  quantity: z.number(),
  oneOff: z.boolean().optional().describe('Define if the line item is a one-off charge, specific only to this order'),
  discountedPrice: z.number().int().max(INT32_MAX).nonnegative().optional().nullable().describe('The discounted price, this should be used as final price if set'),
  discountId: z.string().uuid().optional().nullable().describe('Defines if a discount exists at a line item level'),
  orderBundleLineItems: z.array(OrderBundleLineItemsSchema).optional(),
  deletedAt: z.coerce.date().optional().nullable().describe('When the line item was soft-deleted; populated only for soft-deleted extras (one-off or subscription extras). This is only used by cdc'),
  deletedBy: z.string().optional().nullable().describe('Who deleted the line item; populated only for soft-deleted extras (one-off or subscription extras).'),
  legacyLineItemId: z.number().int().optional().describe('The legacy order line item id from laravel, used for mapping during migration'),
  legacyEntityName: z.enum(['product_orders', 'order_inventory', 'order_inventory_additions']).optional().nullable().describe('The laravel entity this line item came from, used for mapping during migration'),
})

/**
 * One credited line/bucket of a partial refund, stored on `order_refund`.
 * A discriminated union on `kind` because each case carries different fields:
 *
 * - `line` — a real per-line credit (an extra, or a Tyga meal SKU, identified
 *   by the same productId `OrderLineItemSchema` uses).
 * - `mealBucket` — the Lyka volumetric meal bucket, which has no per-SKU
 *   figure, so the whole meal difference is credited as one amount.
 * - `orderAdjustment` — the refund cannot be itemised at all: no new contents
 *   were available to diff against (a retro discount where no line item moved,
 *   or a producer that persists contents separately), an order whose per-line
 *   values pre-date pricing persistence, or a per-line diff that failed the
 *   reconciliation rule below. Producers must record this rather than guess a
 *   breakdown; downstream credits it as a single generic line.
 *
 * The credited amounts are order-owned; the refund money itself lives in
 * `payment.refunds`.
 *
 * **Reconciliation rule.** Within one refund, the credited amounts must sum
 * exactly to the refund amount requested of the payment service — a
 * breakdown that disagrees with the money moved must never be stored. Any
 * cross-check of whether an order has been over-refunded aggregates all of
 * that order's refund rows together, never one row at a time: several rows
 * that each look like a valid partial can still sum above the order's charge.
 *
 * New kinds may be added over time. Consumers should handle an unrecognised
 * `kind` by falling back to the credited amount rather than failing.
 */
export const CreditedLineSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('line'),
    productId: z.string().uuid(),
    quantity: z.number().int().positive(),
    amountCents: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('mealBucket'),
    amountCents: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('orderAdjustment'),
    amountCents: z.number().int().positive(),
  }),
])
export type CreditedLine = z.infer<typeof CreditedLineSchema>

/**
 * Per-animal daily serving size on an order. One entry per animal (not per
 * line item): an order can contain line items for multiple animals, but each
 * animal has a single daily serving size. Snapshotted from the subscription
 * feeding plan at order creation.
 */
export const OrderAnimalServingSchema = z.object({
  id: z.string().uuid().optional(),
  createdTimeUTC: z.coerce.date().optional(),
  updatedTimeUTC: z.coerce.date().optional(),
  animalId: z.string().uuid(),
  dailyServingSizeInGrams: z.number().nonnegative(),
})

export type OrderAnimalServingDto = z.infer<typeof OrderAnimalServingSchema>

export const OrderDiscountSchema = z.object({
  subscriptionDiscountId: z.string().uuid().nullable().optional(),
  discountId: z.string().uuid(),
  orderPosition: z.number().int().nonnegative(),
  promotionUserId: z.string().uuid().optional().nullable(),
  promotionType: z.nativeEnum(PromotionType).optional().nullable(),
  promotionValue: z.string().min(1).max(250).optional().nullable(),

  // only populated when being fetched via orchestration (discounts stitched in)
  name: z.string().optional(),
  description: z.string().optional(),
  interval: z.nativeEnum(DiscountInterval).optional().describe('Is the discount a once-off, hybrid or recurring'),
  currencyCode: z.string().optional(),
})

export type OrderDiscountDto = z.infer<typeof OrderDiscountSchema>

export const OrderReferrerRewardSchema = z.object({
  id: z.string().uuid(),
  rewardId: z.string().uuid(),
  promotionUserId: z.string().uuid(),
  referrerUserId: z.string().uuid(),
  brand: z.nativeEnum(Brand),
  promotionAppliedAt: z.string().datetime(),
  rewardAppliedAt: z.string().datetime().optional().nullable(),
})

export type OrderReferrerRewardDto = z.infer<typeof OrderReferrerRewardSchema>

export const CreateOrderReferrerRewardBodySchema = z.object({
  orderId: z.string().uuid().optional(),
  rewardId: z.string().uuid(),
  promotionUserId: z.string().uuid(),
  referrerUserId: z.string().uuid(),
  brand: z.nativeEnum(Brand),
  promotionAppliedAt: z.string().datetime(),
})

export type CreateOrderReferrerRewardBodySchema = z.infer<typeof CreateOrderReferrerRewardBodySchema>

export const CreateOrderReferrerRewardResponseSchema = z.object({
  id: z.string().uuid(),
})

export type CreateOrderReferrerRewardResponseSchema = z.infer<typeof CreateOrderReferrerRewardResponseSchema>

export const OrderReferrerRewardRecordSchema = OrderReferrerRewardSchema.extend({
  orderId: z.string().uuid().nullable(),
})

export type OrderReferrerRewardRecordDto = z.infer<typeof OrderReferrerRewardRecordSchema>

export const GetOrderReferrerRewardsQuerySchema = z.object({
  referrerUserId: z.string().uuid(),
  brand: z.nativeEnum(Brand).optional(),
  appliedReward: z.boolean().or(
    z.enum(['true', 'false']).transform(val => val === 'true'),
  ).optional(),
})

export type GetOrderReferrerRewardsQuerySchema = z.infer<typeof GetOrderReferrerRewardsQuerySchema>

export const GetOrderReferrerRewardsResponseSchema = z.object({
  referrerRewards: z.array(OrderReferrerRewardRecordSchema),
})

export type GetOrderReferrerRewardsResponseSchema = z.infer<typeof GetOrderReferrerRewardsResponseSchema>

export const UpdateOrderReferrerRewardBodySchema = z.object({
  orderId: z.string().uuid().nullable().optional(),
})

export type UpdateOrderReferrerRewardBodySchema = z.infer<typeof UpdateOrderReferrerRewardBodySchema>

export type OrderLineItemResponseDto = z.infer<typeof OrderLineItemSchema>

export const UpdateOrderLineItemSchema = z.object({
  id: z.string().optional(),
  bundleId: z.string().uuid().nullable().optional(),
  animalId: z.string().uuid().nullable().optional(),
  extraId: z.string().uuid().nullable().optional(),
  productId: z.string().uuid().optional().nullable(),
  productType: z.nativeEnum(ProductType).optional().nullable(),
  planType: z.nativeEnum(FeedingPlanType).nullable().optional(),
  price: z.number().int().nonnegative().optional(),
  unitPrice: z.number().int().max(INT32_MAX).nonnegative().optional().nullable(),
  unitChargePrice: z.number().int().max(INT32_MAX).nonnegative().optional().nullable(),
  totalPrice: z.number().int().max(INT32_MAX).nonnegative().optional().nullable(),
  totalCharge: z.number().int().max(INT32_MAX).nonnegative().optional().nullable(),
  appliedDiscountId: z.string().uuid().optional().nullable(),
  appliedDiscountApplicationId: z.string().uuid().optional().nullable(),
  discountedQuantity: z.number().int().nonnegative().optional().nullable(),
  quantity: z.number().optional(),
  oneOff: z.boolean().optional().describe('Define if the line item is a one-off charge, specific only to this order'),
  discountedPrice: z.number().int().max(INT32_MAX).nonnegative().optional().nullable().describe('The discounted price, this should be used as final price if set'),
  discountId: z.string().uuid().optional().nullable(),
  legacyLineItemId: z.number().int().optional().describe('The legacy order line item id from laravel, used for mapping during migration'),
  legacyEntityName: z.enum(['product_orders', 'order_inventory', 'order_inventory_additions']).optional().nullable().describe('The laravel entity this line item came from, used for mapping during migration'),
  deletedAt: z.coerce.date().optional().nullable().describe('When the line item was soft-deleted; populated only for soft-deleted extras (one-off or subscription extras). This is only used by cdc'),
  deletedBy: z.string().optional().nullable().describe('Who deleted the line item; populated only for soft-deleted extras (one-off or subscription extras).'),
})

export type UpdateOrderLineItemDto = z.infer<typeof UpdateOrderLineItemSchema>

export const OrderMetadataSchema = z.object({
  legacyOrderId: z.number().int().max(INT32_MAX).optional().nullable(),
  phoenixToggle: z.boolean().optional(),
  shadowToggle: z.boolean().optional(),
  isOnHold: z.boolean().optional(),
  isCancelled: z.boolean().optional(),
  // sending the courierId here instead of isBecool boolean, because in future, if data team flags this to be on AUSPOST, we already have this one
  courierId: z.union([z.literal(1), z.literal(3)]).optional()
})

export type OrderMetadataDto = z.infer<typeof OrderMetadataSchema>

export const OrderSchema = z.object({
  subscriptionId: z.string().uuid().optional().nullable(),
  subscriptionHistoryId: z.string().uuid().optional().nullable(),
  userId: z.string().uuid().optional(),
  shipmentId: z.string().optional().nullable(),
  cartNote: z.string().optional().nullable(),
  billingDate: z.coerce.date().optional(),
  billingCutoffDate: z.coerce.date().optional(),
  makeDate: z.coerce.date().optional(),
  totalPrice: z.number().int().nonnegative().optional(),
  totalCharge: z.number().int().nonnegative().optional(),
  planTotalPrice: z.number().int().nonnegative().optional().nullable(),
  planTotalCharge: z.number().int().nonnegative().optional().nullable(),
  priceVersion: z.number().int().positive().optional().nullable(),
  paymentStatus: z.nativeEnum(PaymentStatus).optional(),
  expectedDeliverySlotId: z.string().optional().nullable(),
  deliveryDate: z.coerce.date().optional(),
  deliveryNote: z.string().optional().nullable(),
  deliveryContactNumber: z.string().optional().nullable(),
  rebalancingStatus: z.nativeEnum(RebalancingStatus).optional(),
  source: z.nativeEnum(OrderSource).optional(),
  type: z.nativeEnum(OrderType).optional(),
  fulfilmentStatus: z.nativeEnum(JobStatus).optional(),
  address: AddressSchema.optional(),
  orderLineItems: z.array(OrderLineItemSchema).optional(),
  animalServings: z.array(OrderAnimalServingSchema).optional(),
  depotCode: BeCoolDepotCode.optional(),
  facilityId: z.number().optional(),
  metadata: OrderMetadataSchema.optional(),
  autoChargeAttempt: z.number().int().optional(),
  /** When set, order_reschedule_reasons is written in the same DB transaction as the patch. */
  rescheduleReason: z.lazy(() => RecordOrderRescheduleReasonBodySchema).optional(),
})

export const OrderUpdateWithIdSchema = OrderSchema.extend({
  orderId: z.string().uuid(),
})

export const BatchUpdateOrdersSchema = z.array(OrderUpdateWithIdSchema)

export type BatchUpdateOrdersDto = z.infer<typeof BatchUpdateOrdersSchema>
export type UpdateOrderDto = z.infer<typeof OrderSchema>

export const OrderPaymentStatusSchema = z.object({
  paymentStatus: z.nativeEnum(PaymentStatus),
})

export type OrderPaymentStatusSchema = z.infer<typeof OrderPaymentStatusSchema>

export const OrderFulfilmentStatusSchema = z.object({
  fulfilmentStatus: z.nativeEnum(JobStatus),
})

export type OrderFulfilmentStatusSchema = z.infer<typeof OrderFulfilmentStatusSchema>

export const CreateOrderDiscountBodyBaseSchema = z.object({
  subscriptionDiscountId: z.string().uuid().nullable().optional().describe('Null for partners only'),
  discountId: z.string().uuid(),
  orderPosition: z.number().int().max(INT32_MAX).nonnegative(),
  promotionUserId: z.string().uuid().optional().nullable(),
  promotionType: z.nativeEnum(PromotionType).optional().nullable().describe('Promotion type (referral, coupon) used'),
  promotionValue: z.string().min(1).max(250).optional().nullable().describe('Promotion (coupon) code used. E.g. WELCOME30'),
  // migration use only
  dryRun: z.coerce.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  id: z.string().uuid(),
  orderId: z.string().uuid(),
})

export const CreateOrderDiscountBodySchema = CreateOrderDiscountBodyBaseSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  orderId: true,
}).refine((data) => {
  // when specifying these values, you must set all three properties or not at all
  const promotionUserId = !!data.promotionUserId
  const promotionType = !!data.promotionType
  const promotionValue = !!data.promotionValue

  return (promotionUserId && promotionType && promotionValue)
    || (!promotionUserId && !promotionType && !promotionValue)
}, {
  message: 'All promotion fields must be provided or not at all.',
  path: ['promotionUserId'],
})

export type CreateOrderDiscountBodySchema = z.infer<typeof CreateOrderDiscountBodySchema>

/**
 * Atomically replace an order's discount (remove then optionally add) and patch its totals/line
 * items in one DB transaction. `removeAllDiscount: true` clears every discount on the order
 * (applying a new discount); `removeDiscountIds` removes just those discounts (removing a
 * discount). The two are mutually exclusive.
 */
export const PatchOrderDiscountBodySchema = z.object({
  removeDiscountIds: z.array(z.string().uuid()).default([]),
  removeAllDiscount: z.boolean().default(false),
  discountToAdd: CreateOrderDiscountBodySchema.optional(),
  order: OrderSchema,
}).refine((data) => {
  return !(data.removeAllDiscount && data.removeDiscountIds.length > 0)
}, {
  message: 'removeAllDiscount and removeDiscountIds are mutually exclusive.',
  path: ['removeDiscountIds'],
})
export type PatchOrderDiscountBodySchema = z.infer<typeof PatchOrderDiscountBodySchema>

export const MigrateOrderDiscountSchema = CreateOrderDiscountBodyBaseSchema.omit({
  dryRun: true,
})

export type MigrateOrderDiscountDto = z.infer<typeof MigrateOrderDiscountSchema>

export const OrderInputSchema = z.object({
  subscriptionId: z.string().uuid().optional().nullable(),
  subscriptionHistoryId: z.string().uuid().optional().nullable(),
  userId: z.string().uuid(),
  cartNote: z.string().optional().nullable(),
  billingDate: z.coerce.date(),
  billingCutoffDate: z.coerce.date().optional(),
  totalPrice: z.number().int().max(INT32_MAX).nonnegative(),
  totalCharge: z.number().int().max(INT32_MAX).nonnegative().optional(),
  planTotalPrice: z.number().int().nonnegative().optional().nullable(),
  planTotalCharge: z.number().int().nonnegative().optional().nullable(),
  priceVersion: z.number().int().max(INT32_MAX).positive().optional().nullable(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  expectedDeliverySlotId: z.nativeEnum(DeliverySlotWindow),
  deliveryDate: z.coerce.date(),
  makeDate: z.coerce.date(),
  deliveryNote: z.string().optional(),
  deliveryContactNumber: z.string().optional().nullable(),
  address: AddressSchema, // TODO: this should be AddressInputSchema
  orderLineItems: z.array(OrderLineItemSchema),
  animalServings: z.array(OrderAnimalServingSchema).optional(),
  autoChargeAttempt: z.number().int().max(INT32_MAX).optional(),
  metadata: OrderMetadataSchema.optional(),
  discounts: z.array(CreateOrderDiscountBodySchema).optional().default([]),
  brand: z.nativeEnum(Brand).default(Brand.LYKA),
  source: z.nativeEnum(OrderSource).default(OrderSource.SUBSCRIPTION),
  type: z.nativeEnum(OrderType).default(OrderType.SUBSCRIPTION),
  isDuplicated: z.boolean().optional(),
  fulfilmentStatus: z.nativeEnum(JobStatus).optional()
})

export type OrdersDto = z.infer<typeof OrderInputSchema>

export const OrderAvailableActionsSchema = z.object({
  canCancel: z.boolean(),
  canChangeContents: z.boolean(),
  canChangeAddress: z.boolean(),
  canChangeDeliveryDate: z.boolean().optional(),
  canHold: z.boolean(),
  canTakeOffQueue: z.boolean(),
  canDuplicate: z.boolean(),
})

export type OrderAvailableActionsDto = z.infer<typeof OrderAvailableActionsSchema>

export const OrderResponseSchema = z.object({
  id: z.string().uuid(),
  createdTimeUTC: z.coerce.date(),
  updatedTimeUTC: z.coerce.date(),
  orderNumber: z.number(),
  fulfilmentStatus: z.nativeEnum(JobStatus),
  shipmentId: z.string().optional().nullable(),
  subscriptionId: z.string().uuid().optional().nullable(),
  subscriptionHistoryId: z.string().uuid().optional().nullable(),
  userId: z.string().uuid(),
  cartNote: z.string().optional().nullable(),
  billingDate: z.coerce.date(),
  billedDate: z.coerce.date().optional().nullable(),
  makeDate: z.coerce.date(),
  totalPrice: z.number().int().nonnegative(),
  totalCharge: z.number().int().nonnegative().optional(),
  planTotalPrice: z.number().int().nonnegative().optional().nullable(),
  planTotalCharge: z.number().int().nonnegative().optional().nullable(),
  priceVersion: z.number().int().max(INT32_MAX).positive().optional().nullable(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  expectedDeliverySlotId: z.string().optional(),
  deliveryDate: z.coerce.date(),
  deliveryNote: z.string().optional().nullable(),
  deliveryContactNumber: z.string().optional().nullable(),
  numberOfBoxes: z.number().int().optional().nullable(),
  rebalancingStatus: z.nativeEnum(RebalancingStatus),
  // TODO: this is nullable in the database, but not in the API.
  // see RFC https://www.notion.so/lykapetfood/RFC-Align-TypeScript-Types-with-Database-Column-Definitions-Across-Services-32e78de5664280d2871bce3d9383eaf7
  // for proposed fixes.
  address: AddressSchema,
  orderLineItems: z.array(OrderLineItemSchema),
  animalServings: z.array(OrderAnimalServingSchema).optional(),
  facilityId: z.number(),
  depotCode: BeCoolDepotCode,
  subscriptionOrderIndex: z.number().int().nonnegative().nullable(),
  autoChargeAttempt: z.number().int().optional().nullable(),
  metadata: OrderMetadataSchema.optional(),
  discounts: z.array(OrderDiscountSchema),
  referrerReward: OrderReferrerRewardSchema.optional(),
  brand: z.nativeEnum(Brand),
  source: z.nativeEnum(OrderSource),
  type: z.nativeEnum(OrderType),
  isDuplicated: z.boolean().optional(),
  availableActions: OrderAvailableActionsSchema.optional(),
  isLocked: z.boolean().optional(),
})

export const GetOrdersResponseSchema = generateResultsWithOptionalPagination(OrderResponseSchema)

export type GetOrdersResponse = z.infer<typeof GetOrdersResponseSchema>

export const OrderFlagsResponseSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid(),
  phoenixToggle: z.boolean(),
  shadowToggle: z.boolean(),
})

export const CustomerFlagsResponseSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  phoenixToggle: z.boolean(),
  netsuiteCustomerId: z.string().nullish(),
})

export const OrderFlagsByLegacyIdResponseSchema = z.object({
  orderId: z.string().uuid(),
  legacyOrderId: z.number().int(),
  phoenixToggle: z.boolean(),
  shadowToggle: z.boolean(),
})

export type OrderFlagsByLegacyIdResponseDto = z.infer<typeof OrderFlagsByLegacyIdResponseSchema>

export type OrderResponseDto = z.infer<typeof OrderResponseSchema>

export const CalendarDayBoxesSchema = z.object({
  total: z.number().int().nonnegative(),
  paid: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
})
export type CalendarDayBoxesDto = z.infer<typeof CalendarDayBoxesSchema>

export const CalendarDayBoxesByBrandSchema = z.object({
  lyka: CalendarDayBoxesSchema,
  tyga: CalendarDayBoxesSchema,
})
export type CalendarDayBoxesByBrandDto = z.infer<typeof CalendarDayBoxesByBrandSchema>

export const FutureBoxesByDateSchema = z.record(
  z.string().date(),
  CalendarDayBoxesByBrandSchema,
)
export type FutureBoxesByDateDto = z.infer<typeof FutureBoxesByDateSchema>

export const FutureUnitsByDateInputSchema = z.object({
  productId: z.string().uuid(),
  fromDate: z.string(),
  toDate: z.string(),
  timezone: z.string(),
})

export type FutureUnitsByDateInputSchemaDTO = z.infer<typeof FutureUnitsByDateInputSchema>

export const FutureUnitsByDateSchema = z.array(z.object({ day: z.string(), units: z.number() }))

export type FutureUnitsByDateSchemaDTO = z.infer<typeof FutureUnitsByDateSchema>

export const OrderFiltersSchema = z.object({
  status: z.nativeEnum(PaymentStatus).optional(),
  billingDateRangeStart: z.string().optional(),
  billingDateRangeEnd: z.string().optional(),
  billingDate: z.string().optional().nullable(),
  deliveryDate: z.string().optional(),
  makeDate: z.string().optional(),
  timezone: z.string().optional(),
  userId: z.string().uuid().optional(),
  orderBy: z.enum(['id', 'deliveryDate', 'billedDate', 'createdTimeUTC']).optional(),
  orderDirection: z.enum(['ASC', 'DESC']).optional(),
  subscriptionId: z.string().uuid().optional().nullable(),
  subscriptionOrderIndex: z.coerce.number().int().nonnegative().optional(),
  brand: z.nativeEnum(Brand).optional(),
  source: z.nativeEnum(OrderSource).optional(),
  type: z.nativeEnum(OrderType).optional(),
  phoenixToggle: z.coerce.boolean().optional(),
  hasReferrerReward: z.boolean().or(
    z.enum(['true', 'false']).transform(val => val === 'true'),
  ).optional(),
  hasOrderDiscount: z.boolean().or(
    z.enum(['true', 'false']).transform(val => val === 'true'),
  ).optional(),
  isDuplicated: z.boolean().or(
    z.enum(['true', 'false']).transform(val => val === 'true'),
  ).optional(),
})
  .extend(PaginatedResultsQuerySchema.shape)
  .refine(
    (data) => {
      const hasStart = !!data.billingDateRangeStart
      const hasEnd = !!data.billingDateRangeEnd
      const hasBillingDate = !!data.billingDate
      const hasTimezone = !!data.timezone

      if (hasBillingDate) {
        return hasTimezone
      }

      if (hasStart || hasEnd) {
        return hasStart && hasEnd && hasTimezone
      }

      return true
    },
    {
      message: 'When filtering by date range, provide both start and end with timezone. For billingDate, provide timezone.',
      path: ['billingDateRangeStart'],
    },
  )

export type OrderFiltersDto = z.infer<typeof OrderFiltersSchema>

export const SlimOrderForBulkOperationSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.number().int(),
  subscriptionId: z.string().uuid().nullable(),
  subscriptionOrderIndex: z.number().int().nullable(),
  userId: z.string().uuid(),
  billingDate: z.coerce.date(),
  makeDate: z.coerce.date(),
  deliveryDate: z.coerce.date(),
  timezone: z.string().describe('Address timezone (e.g. Australia/Sydney)'),
  postcode: z.string().describe('Delivery address postcode'),
  suburb: z.string().describe('Delivery address suburb'),
  state: z.nativeEnum(AddressState).describe('Delivery address state (e.g. NSW, WA)'),
  expectedDeliverySlotId: z.string().nullable().describe('Delivery slot window (AM/PM) — nullable on the column'),
  totalCharge: z.number().int().nonnegative(),
  totalPrice: z.number().int().nonnegative(),
  planTotalPrice: z.number().int().nonnegative().optional().nullable(),
  planTotalCharge: z.number().int().nonnegative().optional().nullable(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  autoChargeAttempt: z.number().int().nullable(),
  brand: z.nativeEnum(Brand),
  fulfilmentStatus: z.nativeEnum(JobStatus),
  phoenixToggle: z.boolean().nullable().describe('Customer-level phoenix_toggle flag. null when no customer_flags row exists or when brand is Tyga; otherwise true/false from the customer_flags row.'),
  legacyId: z.number().int().nullable().describe('Laravel legacy order id from order_id_mapping. null when no mapping exists (e.g. native Phoenix orders) or for Tyga orders (post-migration).'),
})

export type SlimOrderForBulkOperationDto = z.infer<typeof SlimOrderForBulkOperationSchema>

export const GetSlimOrdersForBulkOperationQuerySchema = z.object({
  timezone: z.string().min(1).describe('IANA timezone for filtering by local billing date (e.g. Australia/Sydney)'),
  billingDate: z.string().date().describe('Billing date in YYYY-MM-DD interpreted in the given timezone'),
  paymentStatus: z.nativeEnum(PaymentStatus).optional().describe('Filter by order payment_status (PENDING, PROCESSED, ATTEMPT_FAILED, etc.)'),
  autoChargeAttempt: z.coerce.number().int().nonnegative().optional().describe('Filter by exact auto_charge_attempt value'),
  brand: z.nativeEnum(Brand).optional().describe('Filter by brand (Lyka or Tyga)'),
  cursor: z.coerce.number().int().positive().optional().describe('orderNumber from the previous page; returns orders with orderNumber > cursor'),
  limit: z.coerce.number().int().positive().max(500).optional().default(100),
})

export type GetSlimOrdersForBulkOperationQuery = z.infer<typeof GetSlimOrdersForBulkOperationQuerySchema>

export const GetSlimOrdersForBulkOperationResponseSchema = z.object({
  data: z.array(SlimOrderForBulkOperationSchema),
  count: z.number().int().nonnegative().describe('Number of items in this page (= data.length).'),
  nextCursor: z.number().int().nullable().describe('Pass back as cursor for next page. null only when data is empty — paginate until null/empty.'),
})

export type GetSlimOrdersForBulkOperationResponse = z.infer<typeof GetSlimOrdersForBulkOperationResponseSchema>

// ─── Pricing backfill enumeration ──────────────────────────────────────────────
export const OrderPricingBackfillCohort = {
  /** All Tyga-brand orders. */
  TYGA: 'tyga',
  /** Phoenix-generated Lyka orders — brand LYKA, NO `order_id_mapping` row, and customer `phoenix_toggle` = true. */
  LYKA: 'lyka',
  /**
   * All Lyka orders of Phoenix customers — brand LYKA and customer `phoenix_toggle` = true,
   * REGARDLESS of `order_id_mapping` (so both natively-Phoenix and migrated-from-Laravel orders
   * are included). Used by the animal-serving-size backfill.
   */
  LYKA_PHOENIX_CUSTOMER: 'lyka_phoenix_customer',
} as const
export type OrderPricingBackfillCohort = typeof OrderPricingBackfillCohort[keyof typeof OrderPricingBackfillCohort]

export const GetOrdersForPricingBackfillQuerySchema = z.object({
  cohort: z.nativeEnum(OrderPricingBackfillCohort).describe('tyga = all Tyga-brand orders; lyka = Phoenix-generated Lyka orders (phoenix_toggle on + no order_id_mapping / legacyId)'),
  paymentStatus: z.nativeEnum(PaymentStatus).optional().describe('Optional payment_status filter; omit to include all statuses (past + pending)'),
  cursor: z.coerce.number().int().positive().optional().describe('orderNumber from the previous page; returns orders with orderNumber > cursor'),
  limit: z.coerce.number().int().positive().max(500).optional().default(200),
})

export type GetOrdersForPricingBackfillQuery = z.infer<typeof GetOrdersForPricingBackfillQuerySchema>

export const OrderForPricingBackfillSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.number().int(),
  userId: z.string().uuid(),
  subscriptionId: z.string().uuid().nullable(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  brand: z.nativeEnum(Brand),
})

export type OrderForPricingBackfillDto = z.infer<typeof OrderForPricingBackfillSchema>

export const GetOrdersForPricingBackfillResponseSchema = z.object({
  data: z.array(OrderForPricingBackfillSchema),
  count: z.number().int().nonnegative().describe('Number of items in this page (= data.length).'),
  nextCursor: z.number().int().nullable().describe('Pass back as cursor for next page. null only when data is empty — paginate until null/empty.'),
})

export type GetOrdersForPricingBackfillResponse = z.infer<typeof GetOrdersForPricingBackfillResponseSchema>

// ─── Subscription-history backfill ─────────────────────────────────────────────
// Reuses OrderPricingBackfillCohort (tyga = all Tyga; lyka = Phoenix-generated Lyka).

export const GetOrdersForSubscriptionHistoryBackfillQuerySchema = z.object({
  cohort: z.nativeEnum(OrderPricingBackfillCohort).describe('tyga = all Tyga-brand orders; lyka = Phoenix-generated Lyka orders (phoenix_toggle on + no order_id_mapping / legacyId)'),
  paymentStatus: z.nativeEnum(PaymentStatus).optional().describe('Optional payment_status filter; omit to include all statuses'),
  cursor: z.coerce.number().int().positive().optional().describe('orderNumber from the previous page; returns orders with orderNumber > cursor'),
  limit: z.coerce.number().int().positive().max(500).optional().default(200),
})

export type GetOrdersForSubscriptionHistoryBackfillQuery = z.infer<typeof GetOrdersForSubscriptionHistoryBackfillQuerySchema>

export const OrderForSubscriptionHistoryBackfillSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.number().int(),
  userId: z.string().uuid(),
  subscriptionId: z.string().uuid(),
  billedDate: z.coerce.date().nullable(),
  billingDate: z.coerce.date().nullable(),
  brand: z.nativeEnum(Brand),
})

export type OrderForSubscriptionHistoryBackfillDto = z.infer<typeof OrderForSubscriptionHistoryBackfillSchema>

export const GetOrdersForSubscriptionHistoryBackfillResponseSchema = z.object({
  data: z.array(OrderForSubscriptionHistoryBackfillSchema),
  count: z.number().int().nonnegative().describe('Number of items in this page (= data.length).'),
  nextCursor: z.number().int().nullable().describe('Pass back as cursor for next page. null only when data is empty — paginate until null/empty.'),
})

export type GetOrdersForSubscriptionHistoryBackfillResponse = z.infer<typeof GetOrdersForSubscriptionHistoryBackfillResponseSchema>

export const BatchSetSubscriptionHistoryIdBodySchema = z.object({
  updates: z
    .array(
      z.object({
        orderId: z.string().uuid(),
        subscriptionHistoryId: z.string().uuid(),
      }),
    )
    .max(500),
})

export type BatchSetSubscriptionHistoryIdBody = z.infer<typeof BatchSetSubscriptionHistoryIdBodySchema>

export const BatchSetSubscriptionHistoryIdResponseSchema = z.object({
  updated: z.number().int().nonnegative().describe('Number of order rows updated.'),
})

export type BatchSetSubscriptionHistoryIdResponse = z.infer<typeof BatchSetSubscriptionHistoryIdResponseSchema>

// ─── Freeze paid-orders snapshot (forward-path) ────────────────────────────────
// When a subscription's plan changes, every paid order still tracking the live subscription
// (`subscription_history_id = subscription_id`) still reflects the pre-change plan — point them
// all at the snapshot that change wrote, so they don't resolve to the new (wrong) plan.
export const FreezePaidOrdersToSnapshotBodySchema = z.object({
  subscriptionId: z.string().uuid(),
  snapshotId: z.string().uuid().describe('The subscriptions_history snapshot the paid orders should point at.'),
})

export type FreezePaidOrdersToSnapshotBody = z.infer<typeof FreezePaidOrdersToSnapshotBodySchema>

export const FreezePaidOrdersToSnapshotResponseSchema = z.object({
  frozen: z.number().int().nonnegative().describe('Number of paid orders re-pointed from the live subscription to the snapshot.'),
})

export type FreezePaidOrdersToSnapshotResponse = z.infer<typeof FreezePaidOrdersToSnapshotResponseSchema>

// ─── Migrated-order subscription consolidation ─────────────────────────────────
// Migrated (order_id_mapping) Lyka orders on phoenix-toggle customers whose subscription_id
// is still a CDC-derived historical value and whose subscription_history_id is not yet set.

export const GetOrdersForMigratedConsolidationQuerySchema = z.object({
  userId: z.string().uuid().optional().describe('Restrict to a single user (canary/testing); omit to cover all migrated phoenix-toggle users.'),
  cursor: z.coerce.number().int().positive().optional().describe('orderNumber from the previous page; returns orders with orderNumber > cursor'),
  limit: z.coerce.number().int().positive().max(500).optional().default(200),
})

export type GetOrdersForMigratedConsolidationQuery = z.infer<typeof GetOrdersForMigratedConsolidationQuerySchema>

export const OrderForMigratedConsolidationSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.number().int(),
  userId: z.string().uuid(),
})

export type OrderForMigratedConsolidationDto = z.infer<typeof OrderForMigratedConsolidationSchema>

export const GetOrdersForMigratedConsolidationResponseSchema = z.object({
  data: z.array(OrderForMigratedConsolidationSchema),
  count: z.number().int().nonnegative().describe('Number of items in this page (= data.length).'),
  nextCursor: z.number().int().nullable().describe('Pass back as cursor for next page. null only when data is empty — paginate until null/empty.'),
})

export type GetOrdersForMigratedConsolidationResponse = z.infer<typeof GetOrdersForMigratedConsolidationResponseSchema>

export const ConsolidateMigratedOrdersBodySchema = z.object({
  userId: z.string().uuid(),
  liveSubscriptionId: z.string().uuid().describe('The active/live subscription all the user\'s migrated orders are moved onto.'),
})

export type ConsolidateMigratedOrdersBody = z.infer<typeof ConsolidateMigratedOrdersBodySchema>

export const ConsolidateMigratedOrdersResponseSchema = z.object({
  ordersMoved: z.number().int().nonnegative().describe('Migrated orders ported + re-pointed to the live subscription.'),
  ordersReindexed: z.number().int().nonnegative().describe('Total orders re-sequenced under the live subscription (moved + native).'),
})

export type ConsolidateMigratedOrdersResponse = z.infer<typeof ConsolidateMigratedOrdersResponseSchema>

// ─── Phoenix-toggle subscription consolidation (all Lyka phoenix orders) ───────
// Re-points ALL of a phoenix-toggle customer's Lyka orders (migrated + native) onto their
// active/live subscription and re-sequences subscription_order_index. Unlike the migrated
// consolidation this does NOT require an order_id_mapping and does NOT touch
// subscription_history_id. Customers are enumerated by id (each processed exactly once).

export const GetPhoenixToggleCustomerIdsQuerySchema = z.object({
  cursor: z.string().uuid().optional().describe('customer_id from the previous page; returns customers with customer_id > cursor'),
  limit: z.coerce.number().int().positive().max(1000).optional().default(500),
})

export type GetPhoenixToggleCustomerIdsQuery = z.infer<typeof GetPhoenixToggleCustomerIdsQuerySchema>

export const GetPhoenixToggleCustomerIdsResponseSchema = z.object({
  data: z.array(z.string().uuid()).describe('Distinct customer ids with phoenix_toggle = true, ascending.'),
  count: z.number().int().nonnegative().describe('Number of items in this page (= data.length).'),
  nextCursor: z.string().uuid().nullable().describe('Pass back as cursor for next page. null only when data is empty — paginate until null/empty.'),
})

export type GetPhoenixToggleCustomerIdsResponse = z.infer<typeof GetPhoenixToggleCustomerIdsResponseSchema>

export const RepointOrdersToActiveSubscriptionBodySchema = z.object({
  userId: z.string().uuid(),
  activeSubscriptionId: z.string().uuid().describe('The active/live subscription all the user\'s Lyka orders are moved onto.'),
})

export type RepointOrdersToActiveSubscriptionBody = z.infer<typeof RepointOrdersToActiveSubscriptionBodySchema>

export const RepointOrdersToActiveSubscriptionResponseSchema = z.object({
  ordersMoved: z.number().int().nonnegative().describe('Lyka orders re-pointed to the active subscription (were on a different subscription).'),
  ordersReindexed: z.number().int().nonnegative().describe('Total orders re-sequenced under the active subscription (moved + already-there).'),
})

export type RepointOrdersToActiveSubscriptionResponse = z.infer<typeof RepointOrdersToActiveSubscriptionResponseSchema>

// ─── Order cutoff backfill enumeration ────────────────────────────────────────

export const OrderCutoffBackfillCursorSchema = z.object({
  deliveryDate: z.string().datetime().describe('delivery_date of the last row in the previous page (ISO-8601)'),
  orderId: z.string().uuid().describe('order id tie-breaker when delivery_date ties'),
})
export type OrderCutoffBackfillCursor = z.infer<typeof OrderCutoffBackfillCursorSchema>

export const GetOrdersForCutoffBackfillQuerySchema = z.object({
  deliveryDateFrom: z.string().datetime().optional().describe('UTC lower bound on delivery_date; defaults to start of today UTC'),
  cursorDeliveryDate: z.string().datetime().optional().describe('Pagination: delivery_date of last row from previous page'),
  cursorOrderId: z.string().uuid().optional().describe('Pagination: order id tie-breaker from previous page'),
  limit: z.coerce.number().int().positive().max(500).optional().default(200),
  includeNonProcessed: z.boolean().or(
    z.enum(['true', 'false']).transform(val => val === 'true'),
  ).optional().default(false).describe(
    'When true, also include non-processed orders that already have an order_cutoffs row (for stale-cache re-resolve). Processed orders with existing rows are always excluded.',
  ),
}).refine(
  data => (data.cursorDeliveryDate == null) === (data.cursorOrderId == null),
  { message: 'cursorDeliveryDate and cursorOrderId must both be set or both omitted' },
)
export type GetOrdersForCutoffBackfillQuery = z.input<typeof GetOrdersForCutoffBackfillQuerySchema>

export const OrderForCutoffBackfillSchema = z.object({
  id: z.string().uuid(),
  deliveryDate: z.coerce.date(),
  expectedDeliverySlotId: z.enum(['AM', 'PM']),
  brand: z.nativeEnum(Brand),
  postcode: z.string(),
  suburb: z.string(),
  timezone: z.string(),
})
export type OrderForCutoffBackfillDto = z.infer<typeof OrderForCutoffBackfillSchema>

export const GetOrdersForCutoffBackfillResponseSchema = z.object({
  data: z.array(OrderForCutoffBackfillSchema),
  count: z.number().int().nonnegative().describe('Number of items in this page (= data.length).'),
  nextCursor: OrderCutoffBackfillCursorSchema.nullable().describe('Pass back as cursorDeliveryDate + cursorOrderId for next page. null when data is empty.'),
})
export type GetOrdersForCutoffBackfillResponse = z.infer<typeof GetOrdersForCutoffBackfillResponseSchema>

// ─── Meal plan snapshot backfill enumeration ──────────────────────────────────
// Pages paid, Phoenix (customer_flags toggle true OR no row = native), not-yet-started
// orders so orchestration can rebuild the meal plan snapshot on their fulfilments.
// Cursor is a plain order-id walk (order_id ASC) — a full sweep, no delivery-date bound.

export const SnapshotBackfillCursorSchema = z.object({
  orderId: z.string().uuid().describe('order id of the last row in the previous page'),
})
export type SnapshotBackfillCursor = z.infer<typeof SnapshotBackfillCursorSchema>

export const GetOrdersForSnapshotBackfillQuerySchema = z.object({
  cursorOrderId: z.string().uuid().optional().describe('Pagination: order id of the last row from the previous page'),
  limit: z.coerce.number().int().positive().max(500).optional().default(200),
})
export type GetOrdersForSnapshotBackfillQuery = z.input<typeof GetOrdersForSnapshotBackfillQuerySchema>

export const OrderForSnapshotBackfillSchema = z.object({
  id: z.string().uuid(),
})
export type OrderForSnapshotBackfillDto = z.infer<typeof OrderForSnapshotBackfillSchema>

export const GetOrdersForSnapshotBackfillResponseSchema = z.object({
  data: z.array(OrderForSnapshotBackfillSchema),
  count: z.number().int().nonnegative().describe('Number of items in this page (= data.length).'),
  nextCursor: SnapshotBackfillCursorSchema.nullable().describe('Pass back as cursorOrderId for the next page. null when data is empty.'),
})
export type GetOrdersForSnapshotBackfillResponse = z.infer<typeof GetOrdersForSnapshotBackfillResponseSchema>

export const GetDeliveryPromotionCandidatesQuerySchema = z.object({
  timezone: z.string().min(1).describe('IANA timezone; scopes to orders whose delivery address is in this timezone (e.g. Australia/Sydney)'),
  deliveryDate: z.string().date().describe('Local delivery day in YYYY-MM-DD, interpreted in the given timezone'),
  cursor: z.coerce.number().int().positive().optional().describe('orderNumber from the previous page; returns orders with orderNumber > cursor'),
  limit: z.coerce.number().int().positive().max(500).optional().default(100),
})

export type GetDeliveryPromotionCandidatesQuery = z.infer<typeof GetDeliveryPromotionCandidatesQuerySchema>

export const DeliveryPromotionCandidateSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.number().int(),
  userId: z.string().uuid(),
  deliveryDate: z.coerce.date(),
})

export type DeliveryPromotionCandidateDto = z.infer<typeof DeliveryPromotionCandidateSchema>

export const GetDeliveryPromotionCandidatesResponseSchema = z.object({
  data: z.array(DeliveryPromotionCandidateSchema),
  count: z.number().int().nonnegative().describe('Number of items in this page (= data.length).'),
  nextCursor: z.number().int().nullable().describe('Pass back as cursor for next page. null only when data is empty — paginate until null/empty.'),
})

export type GetDeliveryPromotionCandidatesResponse = z.infer<typeof GetDeliveryPromotionCandidatesResponseSchema>

export const ForecastBoxesAtFacilityPayloadSchema = z.object({
  fromDate: z.string().date().optional(),
  dateRangeStart: z.string().date().optional(),
  dateRangeEnd: z.string().date().optional(),
  timezone: z.string().optional(),
}).refine(
  (data) => {
    const hasStart = !!data.dateRangeStart
    const hasEnd = !!data.dateRangeEnd
    const hasFromDate = !!data.fromDate

    const onlyHasFromDate = hasFromDate && !hasStart && !hasEnd
    const onlyHasDateRange = hasStart && hasEnd && !hasFromDate
    return onlyHasFromDate || onlyHasDateRange
  },
  {
    message: 'When filtering by date, provide either dateRangeStart & dateRangeEnd, or fromDate.',
    path: ['fromDate', 'dateRangeStart', 'dateRangeEnd'],
  },
)
export type ForecastBoxesAtFacilityPayloadDto = z.infer<typeof ForecastBoxesAtFacilityPayloadSchema>

export const RebalanceOrdersForDateQuerySchema = z.object({
  date: z.string().date(),
  timezone: z.string(),
})
export type RebalanceOrdersForDateQueryDto = z.infer<typeof RebalanceOrdersForDateQuerySchema>

export const RebalanceOrderRowSchema = z.object({
  orderId: z.string().uuid(),
  userId: z.string().uuid(),
  customerName: z.string(),
  brand: z.nativeEnum(Brand),
  subscriptionFrequencyWeeks: z.number().int().nullable(),
  customerOrderCount: z.number().int(),
  rebalancedBefore: z.boolean(),
  isCustomerMigrated: z.boolean(),
  isOrderMigrated: z.boolean(),
  legacyOrderId: z.number().int().nullable(),
  numberOfBoxes: z.number().int(),
  rebalancingScore: z.number().nullable(),
  chargeDate: z.coerce.date(),
  makeDate: z.coerce.date(),
  deliveryDate: z.coerce.date(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  depot: z.string(),
  manifestCutoff: z.coerce.date().nullable(),
  dispatchCutoff: z.coerce.date().nullable(),
  fulfilmentStatus: z.nativeEnum(JobStatus),
  skuCounts: z.record(z.string(), z.number().int()),
})
export type RebalanceOrderRowDto = z.infer<typeof RebalanceOrderRowSchema>

export const RebalanceOrdersForDateResponseSchema = z.array(RebalanceOrderRowSchema)
export type RebalanceOrdersForDateResponseDto = z.infer<typeof RebalanceOrdersForDateResponseSchema>

export const RebalanceMoveOrderContextSchema = z.object({
  orderId: z.string().uuid(),
  userId: z.string().uuid(),
  orderNumber: z.number().int(),
  makeDate: z.coerce.date(),
  billingDate: z.coerce.date(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  fulfilmentStatus: z.nativeEnum(JobStatus),
  manifestCutoff: z.coerce.date().nullable(),
  dispatchCutoff: z.coerce.date().nullable(),
  isCustomerMigrated: z.boolean(),
  legacyOrderId: z.number().int().nullable(),
  facilityId: z.number().int(),
  numberOfBoxes: z.number().int().nonnegative(),
})
export type RebalanceMoveOrderContextDto = z.infer<typeof RebalanceMoveOrderContextSchema>

export const GetRebalanceMoveContextPayloadSchema = z.object({
  orderIds: z.array(z.string().uuid()).min(1),
})
export type GetRebalanceMoveContextPayloadDto = z.infer<typeof GetRebalanceMoveContextPayloadSchema>

export const GetRebalanceMoveContextResponseSchema = z.object({
  orders: z.array(RebalanceMoveOrderContextSchema),
})
export type GetRebalanceMoveContextResponseDto = z.infer<typeof GetRebalanceMoveContextResponseSchema>

export const UpsertOrderCutoffsSchema = z.object({
  dispatchCutoff: z.coerce.date(),
  manifestCutoff: z.coerce.date(),
  factoryCutoff: z.coerce.date().nullable().optional(),
})
export type UpsertOrderCutoffsDto = z.infer<typeof UpsertOrderCutoffsSchema>

export const OrderFlagsInputSchema = z.object({
  phoenixToggle: z.boolean().optional(),
  shadowToggle: z.boolean().optional(),
  netsuiteCustomerId: z.string().nullish(),
})
export type OrderFlagsInputDto = z.infer<typeof OrderFlagsInputSchema>

export const OrderAction = {
  fulfilment_status_updated: 'fulfilment_status_updated',
  delivery_date_updated: 'delivery_date_updated',
  depot_code_updated: 'depot_code_updated',
  make_date_updated: 'make_date_updated',
} as const
export type OrderAction = typeof OrderAction[keyof typeof OrderAction]

export const OrderEventSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid(),
  description: z.string(),
  action: z.nativeEnum(OrderAction),
  previousValue: z.string().optional().nullable(),
  currentValue: z.string().optional().nullable(),
  createdTimeUTC: z.coerce.date().optional(),
  updatedTimeUTC: z.coerce.date().optional(),
})
export type OrderEventDto = z.infer<typeof OrderEventSchema>

export const CreateOrderEventSchema = z.object({
  description: z.string(),
  action: z.nativeEnum(OrderAction),
  previousValue: z.string().optional(),
  currentValue: z.string().optional(),
})
export type CreateOrderDto = z.infer<typeof CreateOrderEventSchema>

export const AddOrderSubscriptionExtraInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  subscriptionExtra: z.object({
    id: z.string().uuid(),
    productVariantId: z.string().uuid(),
    productType: z.nativeEnum(ProductType).optional(),
    startOrderIndex: z.number().int().gte(0),
    nthOrder: z.number().int().gte(0),
    quantity: z.number().int().gte(1),
    price: z.number().int().gte(0),
  }),
})
export type AddOrderSubscriptionExtraInputDto = z.infer<typeof AddOrderSubscriptionExtraInputSchema>

export const DeleteOrderSubscriptionExtraInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  subscriptionExtraId: z.string().uuid(),
  deletedBy: z.string().optional().nullable().describe('Who deleted the subscription extra'),
})
export type DeleteOrderSubscriptionExtraInputDto = z.infer<typeof DeleteOrderSubscriptionExtraInputSchema>

export const UpdateOrderSubscriptionExtraInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  subscriptionExtra: z.object({
    id: z.string().uuid(),
    quantity: z.number().int().gte(1),
    price: z.number().int().gte(0),
  }),
})
export type UpdateOrderSubscriptionExtraInputDto = z.infer<typeof UpdateOrderSubscriptionExtraInputSchema>

export const MoveOrderSubscriptionExtraInputSchema = z.object({
  subscriptionId: z.string().uuid(),
  subscriptionExtra: z.object({
    id: z.string().uuid(),
    startOrderIndex: z.number().int().gte(0),
    nthOrder: z.number().int().gte(0),
  }),
})
export type MoveOrderSubscriptionExtraInputDto = z.infer<typeof MoveOrderSubscriptionExtraInputSchema>

export const CreateOrderDiscountResponseSchema = z.object({
  id: z.string().uuid(),
})
export type CreateOrderDiscountResponseSchema = z.infer<typeof CreateOrderDiscountResponseSchema>

export const OrderDataMismatchesSchema = z.object({
  path: z.string(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
})

export type OrderDataMismatchesDto = z.infer<typeof OrderDataMismatchesSchema>

export const VerifyOrderResponseSchema = z.object({
  success: z.boolean(),
  userId: z.string().uuid(),
  mismatches: OrderDataMismatchesSchema.array(),
})

export type VerifyOrderResponseDto = z.infer<typeof VerifyOrderResponseSchema>

export const VerifyOrderDiscountResponseSchema = z.object({
  success: z.boolean(),
  mismatches: OrderDataMismatchesSchema.array(),
})

export type VerifyOrderDiscountResponseDto = z.infer<typeof VerifyOrderDiscountResponseSchema>

export const OrdersByLegacyIdsBodySchema = z.object({
  legacyOrderIds: z.array(z.number().int().positive()).describe('Array of laravel legacy order ids'),
})

export type OrdersByLegacyIdsBodyDto = z.infer<typeof OrdersByLegacyIdsBodySchema>

export const OrdersByLegacyIdsResponseSchema = z.object({
  orderIds: z.array(z.object({
    legacyOrderId: z.number().describe('Laravel order id'),
    orderId: z.string().uuid().describe('Phoenix order id'),
    subscriptionOrderIndex: z.number().nullable().describe('Subscription order index'),
  })),
})

export type OrdersByLegacyIdsResponseDto = z.infer<typeof OrdersByLegacyIdsResponseSchema>

// Phoenix toggle orders — reconciliation query schemas
export const GetPhoenixToggleOrdersQuerySchema = z.object({
  makeDateStart: z.string().date().describe('Start of make_date range (YYYY-MM-DD)'),
  makeDateEnd: z.string().date().describe('End of make_date range (YYYY-MM-DD)'),
  facilityIds: FacilityIdsQueryParamSchema,
  paymentStatus: z.nativeEnum(PaymentStatus).optional().describe('Optional payment status filter'),
})

export type GetPhoenixToggleOrdersQuery = z.infer<typeof GetPhoenixToggleOrdersQuerySchema>

export const PhoenixToggleOrderIdSchema = z.object({
  orderId: z.string().uuid().describe('Phoenix order UUID'),
  legacyOrderId: z.number().int().nullable().describe('Mapped Laravel order ID from OrderIdMapping, null if not migrated'),
  numberOfBoxes: z.number().int().nullable().describe('Number of boxes for this order (from orders.number_of_boxes)'),
})

export type PhoenixToggleOrderId = z.infer<typeof PhoenixToggleOrderIdSchema>

export const GetPhoenixToggleOrdersResponseSchema = z.object({
  data: PhoenixToggleOrderIdSchema.array().describe('Order ID pairs with box counts'),
  meta: z.object({
    total: z.number().int(),
    totalBoxes: z.number().int().describe('Total number of boxes across all orders'),
  }),
})

export type GetPhoenixToggleOrdersResponse = z.infer<typeof GetPhoenixToggleOrdersResponseSchema>

export const GetUpcomingOrdersForProductionDateAlertQuerySchema = z.object({
  makeDateStart: z.string().date().describe('Start of make_date range (YYYY-MM-DD)'),
  makeDateEnd: z.string().date().describe('End of make_date range (YYYY-MM-DD)'),
  facilityIds: FacilityIdsQueryParamSchema,
})
export type GetUpcomingOrdersForProductionDateAlertQuery = z.infer<typeof GetUpcomingOrdersForProductionDateAlertQuerySchema>

export const UpcomingOrderWithDatesSchema = z.object({
  orderId: z.string().uuid(),
  legacyOrderId: z.number().int().nullable(),
  makeDate: z.string().describe('ISO-8601 timestamp with timezone'),
  deliveryDate: z.string().describe('ISO-8601 timestamp with timezone'),
  facilityId: z.number().int(),
  depotCode: BeCoolDepotCode,
  state: z.nativeEnum(AddressState).nullable().describe('Delivery address state (e.g., TAS, VIC)'),
  brand: z.nativeEnum(Brand),
  timezone: z.string().describe('Address timezone (e.g., Australia/Sydney)'),
})
export type UpcomingOrderWithDates = z.infer<typeof UpcomingOrderWithDatesSchema>

export const GetUpcomingOrdersForProductionDateAlertResponseSchema = z.object({
  data: UpcomingOrderWithDatesSchema.array(),
  meta: z.object({
    total: z.number().int(),
  }),
})
export type GetUpcomingOrdersForProductionDateAlertResponse = z.infer<typeof GetUpcomingOrdersForProductionDateAlertResponseSchema>

export const BackfillProductTypeInputSchema = z.object({
  batchSize: z.number().int().positive().optional().default(10000),
})
export type BackfillProductTypeInput = z.infer<typeof BackfillProductTypeInputSchema>

export const BackfillProductTypeOutputSchema = z.object({
  phase: z.enum(['progress', 'done', 'stuck']),
  rowsAffected: z.number().int().nonnegative(),
})
export type BackfillProductTypeOutput = z.infer<typeof BackfillProductTypeOutputSchema>

export const DeleteMigratedOrderDiscountsResponseSchema = z.object({
  deletedCount: z.number().int().nonnegative(),
  dryRun: z.boolean(),
})

export const OUTBOX_STATUS_VALUES = [
  'PENDING',
  'CLAIMED',
  'PROCESSING',
  'SUCCEEDED',
  'RETRYABLE_FAILED',
  'FAILED',
  'BLOCKED',
] as const

export type OutboxStatus = typeof OUTBOX_STATUS_VALUES[number]

// Compile-time enforcement of valid event_type values; no DB CHECK constraint
// so new event types can land without a schema migration. Add entries here
// as each NetSuite resource gets its own producer. The dot/colon namespace
// ("order.paid:deposit" reads as "the deposit variant of order.paid") is
// intentionally distinct from the SNS-based `order_paid` notification event
// in OrderMessageBodyNameEnum (a separate pipeline for Customer.io emails).
//
// V1 outbox chain (per the OPS-3008 RFC). All events are order-aggregate —
// every NetSuite document in the chain is driven by a status transition on
// the Phoenix Order, mirroring how Laravel's existing NetSuite sync works.
//
//   order.paid                     -> NetSuite Sales Order        (producer wired)
//   order.paid:deposit             -> NetSuite Customer Deposit   (producer wired)
//   order.palletised               -> NetSuite Item Fulfilment    (producer wired)
//   order.delivered:invoice        -> NetSuite Sales Invoice      (producer wired)
//
// Refund reversals (one row per fully-refunded order, keyed per refund):
//
//   order.refunded:customerRefund  -> Customer Refund transformed from the
//                                     Customer Deposit (deposit-only orders)
//   order.refunded:creditMemo      -> Credit Memo + Customer Refund
//
// Additional charges (one row per extra payment taken on an already-paid
// order; an order can hold several):
//
//   order.charged:deposit          -> a second Customer Deposit, then an
//                                     amendment of the Sales Order to the
//                                     order's new lines
//
// Producer status reflects what writes a row into `outbox` today. The consumer
// side is `OUTBOX_EVENT_HANDLERS` in the orchestration app's
// processNetsuiteOutboxEvent.ts: one entry per type, `null` for a type whose
// producer ships ahead of its dispatcher branch. The poller claims by the list
// derived from it, so a type listed here but not yet handled there simply waits.
export const OUTBOX_EVENT_TYPES = [
  'order.paid',
  'order.paid:deposit',
  'order.palletised',
  'order.delivered:invoice',
  'order.refunded:customerRefund',
  'order.refunded:creditMemo',
  'order.charged:deposit',
] as const

export type OutboxEventType = typeof OUTBOX_EVENT_TYPES[number]

// NetSuite mapping enums. Shared between the order service (DB schema for
// `netsuite_internal_ids`) and the orchestration service (activity outputs
// and workflow types). Add new resource types here as each NetSuite resource
// gets its own producer.
export const NETSUITE_RESOURCE_TYPES = [
  'customer',
  'salesOrder',
  'customerDeposit',
  'itemFulfillment',
  'salesInvoice',
  'depositApplication',
  'customerRefund',
  'creditMemo',
] as const
export type NetsuiteResourceType = typeof NETSUITE_RESOURCE_TYPES[number]

/**
 * Provenance of a `netsuite_internal_ids` row. Lives in contracts so all
 * apps that touch the mapping table (order service for the DB schema,
 * orchestration for activity outputs) share one definition. The
 * `Extract<NetsuiteIdSource, 'created' | 'alreadyExists'>` narrowings in
 * apps/orchestration depend on this being the canonical source of truth.
 *
 * Orchestration activities only ever produce `created` or `alreadyExists`;
 * `discovered` is reserved for out-of-band reconciliation paths that bypass
 * the outbox pipeline (writer not yet wired).
 *
 * The same value set is mirrored on the order DB schema via a string CHECK
 * constraint (`CK_netsuite_internal_ids_source`), chosen over a Postgres
 * ENUM type because ENUMs are awkward to extend. Adding or removing a value
 * here requires three coordinated updates:
 *
 *   1. The CHECK constraint clause in
 *      apps/order/src/migrations/1779494500000-generalise-outbox-for-erp.ts
 *   2. The matching `@Check` decorator on the `NetsuiteInternalId` entity in
 *      apps/order/src/modules/orders/entities/netsuite-internal-id.entity.ts
 *   3. The `source` column comment (both in the migration and on the
 *      entity's `@Column`), if the new value carries a semantic note.
 */
export const NETSUITE_ID_SOURCES = ['created', 'alreadyExists', 'discovered'] as const
export type NetsuiteIdSource = typeof NETSUITE_ID_SOURCES[number]

export const ClaimedOutboxEventSchema = z.object({
  id: z.string(),
  eventType: z.enum(OUTBOX_EVENT_TYPES),
  orderId: z.string().uuid(),
  userId: z.string().uuid(),
  payload: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  claimedAt: z.string().datetime(),
  workflowId: z.string(),
  // Attempt count *after* the claim's increment (so the first claim's DTO
  // carries `attemptCount: 1`). The dispatcher uses this to compute
  // exponential backoff via `markOutboxFailed`.
  attemptCount: z.number().int().nonnegative(),
})
export type ClaimedOutboxEventDto = z.infer<typeof ClaimedOutboxEventSchema>

export const ClaimOutboxBatchBodySchema = z.object({
  limit: z.number().int().positive().max(1000).optional().default(100),
  // The event types the caller's dispatcher can process. Omit to claim any
  // type, which is how every caller behaved before this field existed.
  //
  // Naming them is what lets a producer ship before its consumer: a type the
  // caller leaves out stays PENDING and unstamped until a build that knows it
  // asks for it, instead of being claimed and failed as an unknown type. An
  // empty list is refused because it reads as "I can process nothing" and
  // would stall the whole pipeline silently.
  eventTypes: z.array(z.enum(OUTBOX_EVENT_TYPES)).min(1).optional(),
})
export type ClaimOutboxBatchBody = z.infer<typeof ClaimOutboxBatchBodySchema>

export const ClaimOutboxBatchResponseSchema = z.object({
  items: z.array(ClaimedOutboxEventSchema),
})
export type ClaimOutboxBatchResponse = z.infer<typeof ClaimOutboxBatchResponseSchema>

/**
 * Optional block on `markOutboxProcessed`: the consumer activity asks the
 * order service to write the `netsuite_internal_ids` row in the same DB
 * transaction as the row's SUCCEEDED transition (the RFC's "two writes, one
 * transaction"). `order_id` and `source_outbox_id` are derived from the
 * outbox row itself, so the caller only sends the NetSuite-side fields.
 *
 * `source` is restricted to `created`/`alreadyExists` here — every row that
 * has a producing outbox row went through a Phoenix POST, so it always has a
 * `phoenixExternalId`. The `discovered` provenance (no outbox row) goes
 * through `recordDiscoveredNetsuiteInternalId` instead.
 *
 * A row is scoped in exactly one of three ways, and the scope decides which
 * partial unique index the order service upserts against: order-scoped (both
 * ids absent), refund-scoped (`refundId`), payment-scoped (`paymentId`). The
 * refines mirror CK_netsuite_internal_ids_payment_id and the exclusivity the
 * two CHECKs enforce together, so a bad combination is a clean 400 rather than
 * a DB constraint 500 raised inside the transaction that marks the outbox row
 * succeeded.
 */
export const MarkOutboxProcessedNetsuiteInternalIdSchema = z.object({
  resourceType: z.enum(NETSUITE_RESOURCE_TYPES),
  netsuiteInternalId: z.string().min(1),
  phoenixExternalId: z.string().min(1),
  source: z.enum(['created', 'alreadyExists']),
  // payment.refunds id, present only for refund-scoped resource types
  // (customerRefund, creditMemo) so one order can map several reversals.
  refundId: z.string().uuid().optional(),
  // payment.payments id, present only on the extra Customer Deposits posted
  // for additional charges, so one order can map several deposits. The
  // original deposit is order-scoped and leaves this absent.
  paymentId: z.string().uuid().optional(),
}).refine(
  v => v.paymentId === undefined || v.resourceType === 'customerDeposit',
  { message: 'paymentId is only valid on a customerDeposit', path: ['paymentId'] },
).refine(
  v => v.refundId === undefined || v.paymentId === undefined,
  { message: 'refundId and paymentId are mutually exclusive scopes', path: ['paymentId'] },
)
export type MarkOutboxProcessedNetsuiteInternalId = z.infer<typeof MarkOutboxProcessedNetsuiteInternalIdSchema>

/**
 * One ref, or a list of refs, written in the same transaction as the row's
 * SUCCEEDED transition. Most rows map a single NetSuite record; the invoiced
 * refund maps two in one dispatcher run (the Credit Memo, and the Customer
 * Refund applied to it), so the field accepts either shape. Kept on the same
 * `netsuiteInternalId` key so existing single-ref callers are unchanged: a
 * mid-deploy order service that only understands the single shape rejects an
 * array (Temporal retries) rather than silently stripping and dropping a
 * mapping.
 */
export const MarkOutboxProcessedNetsuiteInternalIdsSchema = z.union([
  MarkOutboxProcessedNetsuiteInternalIdSchema,
  z.array(MarkOutboxProcessedNetsuiteInternalIdSchema),
])

/** Collapse the single-or-array ref field to an array the caller can iterate. */
export function normaliseNetsuiteInternalIdRefs(
  refs: MarkOutboxProcessedNetsuiteInternalId | MarkOutboxProcessedNetsuiteInternalId[] | undefined,
): MarkOutboxProcessedNetsuiteInternalId[] {
  if (refs === undefined)
    return []
  return Array.isArray(refs) ? refs : [refs]
}

export const MarkOutboxProcessedBodySchema = z.object({
  succeededAt: z.coerce.date(),
  // Optional write-back read by downstream rows in the same DAG via
  // `predecessor_id`. The consumer activity decides the shape per event_type;
  // we store it as opaque JSON here so the order service stays decoupled
  // from the SO/Deposit/Invoice shape evolution.
  resultPayload: z.record(z.unknown()).optional(),
  // Optional ID-row write(s) — see MarkOutboxProcessedNetsuiteInternalIdsSchema.
  // A single ref or an array; the success transition and every ID-row UPSERT
  // run in one tx.
  netsuiteInternalId: MarkOutboxProcessedNetsuiteInternalIdsSchema.optional(),
})
export type MarkOutboxProcessedBody = z.infer<typeof MarkOutboxProcessedBodySchema>

/**
 * Body for the dispatcher's failure callback. Drives the outbox state
 * machine's terminal transitions:
 *   - retryable=true  → status='RETRYABLE_FAILED', claimed_at reset, available_at
 *                       set so the row becomes re-claimable after backoff.
 *   - retryable=false → status='FAILED', terminal; needs replay tooling or a
 *                       manual operator transition to retry.
 *
 * Truncate `failureMessage` to a reasonable length (Honeycomb event size,
 * audit log readability). The dispatcher passes ApplicationFailure.message
 * truncated to 500 chars.
 */
export const MarkOutboxFailedBodySchema = z.object({
  retryable: z.boolean(),
  failureMessage: z.string().max(2000),
  // Required when retryable=true (backoff target); ignored when retryable=false.
  availableAt: z.coerce.date().optional(),
})
export type MarkOutboxFailedBody = z.infer<typeof MarkOutboxFailedBodySchema>

// Returned by `loadOutboxPredecessor` — the deposit-row consumer reads its
// predecessor (the SO row) to pick up the NetSuite ids and post-rounding
// total earned by the SO POST. Returned shapes are intentionally opaque
// `Record<string, unknown>`; the deposit activity is the only place that
// knows how to Zod-validate the SO-specific `SalesOrderResultPayloadSchema`,
// keeping the contract package free of consumer-shape coupling.
export const OutboxPredecessorSchema = z.object({
  id: z.string(),
  eventType: z.enum(OUTBOX_EVENT_TYPES),
  orderId: z.string().uuid(),
  status: z.enum(OUTBOX_STATUS_VALUES),
  payload: z.record(z.unknown()),
  resultPayload: z.record(z.unknown()).nullable(),
})
export type OutboxPredecessor = z.infer<typeof OutboxPredecessorSchema>

/**
 * Whether a later row of the same kind has already succeeded for this order, and
 * if so which one. `supersededBy` is that row's id, newest first, or null.
 *
 * Only `order.charged:deposit` rows can answer anything but null: they are the
 * one event type an order holds several of, so they are the only kind whose work
 * a later sibling can render out of date. Each charge row carries a snapshot of
 * the whole order, and posting it replaces the Sales Order's lines outright, so a
 * row that has been overtaken must not send its own older snapshot.
 */
export const OutboxSupersedingRowSchema = z.object({
  supersededBy: z.string().nullable(),
})
export type OutboxSupersedingRow = z.infer<typeof OutboxSupersedingRowSchema>

// ─── netsuite_internal_ids endpoints ─────────────────────────────────────
//
// Read-before-write idempotency for the NetSuite outbox chain. Each row maps
// one Phoenix order + resource type to the NetSuite internalId created for it.
// The `id` and `sourceOutboxId` fields are bigint in the DB, surfaced as
// strings to avoid JS number-precision loss (same convention as the outbox
// DTOs).

export const NetsuiteInternalIdSchema = z.object({
  id: z.string(),
  orderId: z.string().uuid(),
  resourceType: z.enum(NETSUITE_RESOURCE_TYPES),
  netsuiteInternalId: z.string(),
  phoenixExternalId: z.string().nullable(),
  source: z.enum(NETSUITE_ID_SOURCES),
  sourceOutboxId: z.string().nullable(),
  // payment.refunds id for refund-scoped resource types; null for the
  // order-scoped chain records (SO, deposit, IF, invoice, customer).
  refundId: z.string().uuid().nullable(),
  // payment.payments id for the extra Customer Deposits posted against
  // additional charges; null on every other row, including the order's
  // original deposit.
  paymentId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
})
export type NetsuiteInternalIdDto = z.infer<typeof NetsuiteInternalIdSchema>

// GET /netsuite-internal-ids?orderId=&resourceType= — read-before-write check
// run by every NetSuite POST activity.
export const GetNetsuiteInternalIdQuerySchema = z.object({
  orderId: z.string().uuid(),
  resourceType: z.enum(NETSUITE_RESOURCE_TYPES),
})
export type GetNetsuiteInternalIdQuery = z.infer<typeof GetNetsuiteInternalIdQuerySchema>

// GET /netsuite-internal-ids/order/:orderId — all NS ids for an order in one
// round-trip (the SI activity needs SO + IF ids; ops debugging).
export const ListNetsuiteInternalIdsResponseSchema = z.object({
  items: z.array(NetsuiteInternalIdSchema),
})
export type ListNetsuiteInternalIdsResponse = z.infer<typeof ListNetsuiteInternalIdsResponseSchema>

// POST /netsuite-internal-ids/discovered — the two cases with no producing
// outbox row: a `discovered` record (NetSuite auto-created it, no Phoenix
// externalId) and the customer-creation case (`created`/`alreadyExists`, no
// source outbox). The refine mirrors CK_netsuite_internal_ids_external_id_source
// so a bad combination is a clean 400 rather than a DB constraint 500.
export const RecordDiscoveredNetsuiteInternalIdBodySchema = z.object({
  orderId: z.string().uuid(),
  resourceType: z.enum(NETSUITE_RESOURCE_TYPES),
  netsuiteInternalId: z.string().min(1),
  source: z.enum(NETSUITE_ID_SOURCES),
  phoenixExternalId: z.string().min(1).nullable(),
}).refine(
  v => (v.source === 'discovered') === (v.phoenixExternalId === null),
  { message: 'phoenixExternalId must be null when source is \'discovered\', and non-null otherwise', path: ['phoenixExternalId'] },
)
export type RecordDiscoveredNetsuiteInternalIdBody = z.infer<typeof RecordDiscoveredNetsuiteInternalIdBodySchema>

// ─── outbox_replay admin endpoint ────────────────────────────────────────
//
// POST /admin/outbox/:id/replay — writes an audit row plus a fresh PENDING
// outbox row in one tx. The source row is never mutated; the new row's
// idempotency_key is `<orig>:replay:<source_id>`, so a second replay of the
// same row hits UQ_outbox_idempotency_key (returned as 409). triggeredBy is
// supplied in the body until apps/order grows auth wiring.
export const ReplayOutboxRowBodySchema = z.object({
  reason: z.string().min(1).max(2000),
  triggeredBy: z.string().min(1).max(255),
})
export type ReplayOutboxRowBody = z.infer<typeof ReplayOutboxRowBodySchema>

export const ReplayOutboxRowResponseSchema = z.object({
  replayId: z.string(),
  sourceId: z.string(),
  newOutboxId: z.string(),
})
export type ReplayOutboxRowResponse = z.infer<typeof ReplayOutboxRowResponseSchema>

// ─── outbox backfill admin endpoint ──────────────────────────────────────
//
// POST /admin/orders/:orderId/outbox/backfill — manufactures the outbox rows
// an already-terminal order is missing, derived from its current payment and
// fulfilment status (no state transition is replayed). Requires the order's
// netsuiteOutboxToggle to be ON — the operator flips the cohort on first, and
// the endpoint never writes the toggle itself. Rows carry the canonical
// idempotency keys, so re-running an order is a no-op and a partially-synced
// order gets only its missing tail.

export const BACKFILL_OUTBOX_SKIP_REASONS = ['toggle_off', 'not_processed', 'no_boxes'] as const
export type BackfillOutboxSkipReason = typeof BACKFILL_OUTBOX_SKIP_REASONS[number]

export const BACKFILL_OUTBOX_STAGE_OUTCOMES = ['planned', 'inserted', 'already_present'] as const
export type BackfillOutboxStageOutcome = typeof BACKFILL_OUTBOX_STAGE_OUTCOMES[number]

export const BackfillOutboxBodySchema = z.object({
  dryRun: z.boolean().optional().default(false),
})
export type BackfillOutboxBody = z.infer<typeof BackfillOutboxBodySchema>

export const BackfillOutboxStageResultSchema = z.object({
  eventType: z.enum(OUTBOX_EVENT_TYPES),
  // `planned` only appears on dry runs; a write run reports `inserted` or
  // `already_present` per stage.
  outcome: z.enum(BACKFILL_OUTBOX_STAGE_OUTCOMES),
})
export type BackfillOutboxStageResult = z.infer<typeof BackfillOutboxStageResultSchema>

export const BackfillOutboxResponseSchema = z.object({
  orderId: z.string().uuid(),
  dryRun: z.boolean(),
  // Set when the order was skipped whole (nothing written): toggle not yet
  // flipped on, not yet paid, or missing a field the payloads require.
  // Mirrors the live subscriber's guards.
  skippedReason: z.enum(BACKFILL_OUTBOX_SKIP_REASONS).nullable(),
  stages: z.array(BackfillOutboxStageResultSchema),
})
export type BackfillOutboxResponse = z.infer<typeof BackfillOutboxResponseSchema>

// ─── NetSuite sync summary (daily digest) ────────────────────────────────
//
// GET /admin/netsuite-sync/daily-summary — the counts behind the daily Slack
// digest. Reads the order DB only and makes no NetSuite call, so every
// "synced" figure means "Phoenix recorded a NetSuite id", never "the record
// exists in NetSuite".
//
// Two cohorts, because they answer different questions:
//
//   paidToday — orders whose billed_date lands on the requested calendar day
//     in the requested timezone. Sales Orders and Customer Deposits are due
//     the moment an order is paid, so absence here is immediately actionable.
//
//   lifecycle — toggled orders that reached the relevant fulfilment status on
//     the requested day or the day before, scoped by the day the stage happens
//     on: make_date for Item Fulfilments (a job packed today palletises
//     today), delivery_date for invoices (delivered-promotion targets today's
//     deliveries). Day-scoping keeps the cohort two days wide instead of
//     growing with every order ever delivered; the extra day lets the next
//     morning's runs catch a miss that appeared after the last evening check.
//     The denominator is chain-consistent: an order only counts as due once
//     its predecessor record is synced. That mirrors the dispatcher's claim
//     gate and keeps orders palletised before the outbox existed out of the
//     numbers instead of flagging them as missing every single day.

export const NETSUITE_SYNC_ATTENTION_KINDS = [
  // Terminal outbox states — need a replay.
  'failed',
  'blocked',
  // Due but no outbox row at all — the silent producer-bypass class; needs a backfill.
  'missing',
  // Still in flight but far past the poll cadence, so it is not self-healing.
  'stuck',
  // A record that exists when the due rules say it should not (e.g. a Customer
  // Deposit on a zero-charge order, or any record on a failed charge).
  'unexpected',
] as const
export type NetsuiteSyncAttentionKind = typeof NETSUITE_SYNC_ATTENTION_KINDS[number]

/**
 * Counts that partition the due set exactly:
 * `due === synced + inFlight + failed + blocked + missing`.
 *
 * `synced` counts confirmed `netsuite_internal_ids` rows, not SUCCEEDED
 * outbox rows — a zero-charge order's deposit row succeeds via
 * `skippedZeroAmount` without writing a NetSuite id, so counting rows would
 * break the partition. `inFlight` covers PENDING, CLAIMED, PROCESSING and
 * RETRYABLE_FAILED, all of which self-heal on the next poll tick.
 */
export const NetsuiteSyncRecordCountsSchema = z.object({
  due: z.number().int().nonnegative(),
  synced: z.number().int().nonnegative(),
  inFlight: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative(),
})
export type NetsuiteSyncRecordCounts = z.infer<typeof NetsuiteSyncRecordCountsSchema>

export const NetsuiteSyncAttentionItemSchema = z.object({
  kind: z.enum(NETSUITE_SYNC_ATTENTION_KINDS),
  // Null when the anomaly is not tied to one outbox row — a failed-charge order
  // whose only trace is a netsuite_internal_ids row has no event type to name.
  eventType: z.enum(OUTBOX_EVENT_TYPES).nullable(),
  orderId: z.string().uuid(),
  // Ops act on order numbers, not UUIDs — both are returned so the digest can
  // print the number while remediation endpoints take the id.
  orderNumber: z.number().int(),
  // Set on refund items only. An order can accrue several reversals, one per
  // partial refund, so the order id alone no longer identifies the item — two
  // missing partials on one order would otherwise be indistinguishable.
  refundId: z.string().uuid().nullable().optional(),
  // Null for `missing`: there is no row to point at.
  outboxId: z.string().nullable(),
  status: z.enum(OUTBOX_STATUS_VALUES).nullable(),
  lastFailureMessage: z.string().nullable(),
  // Null on lifecycle-stage items for orders with no billed_date.
  billedAt: z.string().datetime().nullable(),
  // Set for `stuck` only — hours since the row was created (claimed_at resets
  // on every retry loop, so it would keep a looping row looking fresh).
  ageHours: z.number().nullable(),
})
export type NetsuiteSyncAttentionItem = z.infer<typeof NetsuiteSyncAttentionItemSchema>

export const NetsuiteSyncSummaryQuerySchema = z.object({
  // Explicit calendar day: the caller decides the cohort, never the server clock.
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
  timezone: z.string().optional().default('Australia/Melbourne'),
})
export type NetsuiteSyncSummaryQuery = z.infer<typeof NetsuiteSyncSummaryQuerySchema>

export const NetsuiteSyncSummaryResponseSchema = z.object({
  date: z.string(),
  timezone: z.string(),
  paidToday: z.object({
    // Every order paid that day, whatever its toggle — the outer denominator.
    paidOrders: z.number().int().nonnegative(),
    // The netsuite_outbox_toggle subset; every count below is drawn from this.
    inScope: z.number().int().nonnegative(),
    inScopeByBrand: z.array(z.object({
      brand: z.nativeEnum(Brand),
      count: z.number().int().nonnegative(),
    })),
    salesOrders: NetsuiteSyncRecordCountsSchema,
    // Cohort excludes zero-charge orders: NetSuite refuses zero-amount payment
    // records, so a free order is never deposit-due.
    customerDeposits: NetsuiteSyncRecordCountsSchema,
    // Reported separately because their expectation differs: a Sales Order and
    // no deposit. A deposit that does exist lands in needsAttention.
    freeOrders: z.object({
      paid: z.number().int().nonnegative(),
      salesOrdersSynced: z.number().int().nonnegative(),
    }),
    // Cohort is order_refund rows created that day — refunds carry their own
    // insert timestamp, so they get a real daily due-set of their own.
    refunds: NetsuiteSyncRecordCountsSchema,
  }),
  lifecycle: z.object({
    itemFulfilments: NetsuiteSyncRecordCountsSchema,
    invoices: NetsuiteSyncRecordCountsSchema,
  }),
  // All days, not just the requested one — a row that failed terminally last
  // week stays invisible otherwise.
  backlog: z.object({
    failed: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    stuck: z.number().int().nonnegative(),
  }),
  // The inverse assertion: orders whose charge failed must have produced no
  // outbox rows and no NetSuite ids.
  leakCheck: z.object({
    attemptFailedOrders: z.number().int().nonnegative(),
    leakedRecords: z.number().int().nonnegative(),
  }),
  // Capped list; `needsAttentionTotal` stays exact so the digest can say how
  // many were withheld.
  needsAttention: z.array(NetsuiteSyncAttentionItemSchema),
  needsAttentionTotal: z.number().int().nonnegative(),
})
export type NetsuiteSyncSummaryResponse = z.infer<typeof NetsuiteSyncSummaryResponseSchema>

// ─── order_refund endpoints ──────────────────────────────────────────────
//
// Order-side record of a completed payment refund: one row per refund, for
// full and partial alike. The row holds refundId + creditedLines only — the
// money (amount/status) stays in `payment.refunds`, fetched by refundId. The
// refunded amount is supplied on the record call so the credited lines can be
// reconciled against it there; on a stored partial row the credited amounts
// therefore already sum to the money refunded.
// `creditedLines: null` means a full refund (the NetSuite reversal mirrors
// the whole Sales Order). The `id` field is bigint in the DB, surfaced as a
// string to avoid JS number-precision loss (same convention as the outbox
// and netsuite_internal_ids DTOs).

export const OrderRefundSchema = z.object({
  id: z.string(),
  orderId: z.string().uuid(),
  refundId: z.string().uuid(),
  creditedLines: z.array(CreditedLineSchema).nullable(),
  createdAt: z.string().datetime(),
})
export type OrderRefundDto = z.infer<typeof OrderRefundSchema>

// POST /orders/:orderId/refunds — orchestration records the completed-refund
// fact once the Stripe refund settles. creditedLines is required-but-nullable
// so a full refund is an explicit `null`, never an accidental omission; a
// partial refund must credit at least one line.
//
// `refundAmountCents` is the money the refund actually moved: the same amount
// the caller asked the payment service to refund. Order stores the breakdown
// but not the money, so this is the only figure it can reconcile the credited
// lines against, and a partial whose lines disagree with it is rejected rather
// than recorded (see the reconciliation rule on CreditedLineSchema).
//
// Required for a partial, optional for a full refund: a full reversal mirrors
// the whole document, so nothing reconciles against the amount and it is never
// read. That also keeps a caller that only records full refunds working while
// a release is still rolling out and both versions are live.
export const RecordOrderRefundBodySchema = z.object({
  refundId: z.string().uuid(),
  refundAmountCents: z.number().int().positive().optional(),
  creditedLines: z.array(CreditedLineSchema).min(1).nullable(),
}).refine(
  body => body.creditedLines === null || body.refundAmountCents !== undefined,
  { message: 'refundAmountCents is required for a partial refund', path: ['refundAmountCents'] },
)
export type RecordOrderRefundBody = z.infer<typeof RecordOrderRefundBodySchema>

// GET /orders/:orderId/refunds — all refund records for an order (deriving
// refund state, and the optional REFUNDED display label).
export const ListOrderRefundsResponseSchema = z.object({
  items: z.array(OrderRefundSchema),
})
export type ListOrderRefundsResponse = z.infer<typeof ListOrderRefundsResponseSchema>

// ─── order_charge endpoints ──────────────────────────────────────────────
//
// Order-side record of an additional charge: one row per extra payment taken
// against an already-paid order. The row is a pointer, not a ledger — it holds
// the payment id and nothing else, so the money cannot drift from the payment
// service, which stays its source of truth and is read by that id at dispatch
// time. There is no per-line breakdown either: the NetSuite Sales Order
// amendment re-snapshots the whole order rather than diffing lines.
// The `id` field is bigint in the DB, surfaced as a string to avoid JS
// number-precision loss (same convention as the outbox and order_refund DTOs).

export const OrderChargeSchema = z.object({
  id: z.string(),
  orderId: z.string().uuid(),
  paymentId: z.string().uuid(),
  createdAt: z.string().datetime(),
})
export type OrderChargeDto = z.infer<typeof OrderChargeSchema>

// POST /orders/:orderId/charges — orchestration records the fact once the
// extra Stripe charge completes. The payment id is the whole body: it is both
// the identity the row is idempotent on and the handle the dispatcher fetches
// the amount by.
export const RecordOrderChargeBodySchema = z.object({
  paymentId: z.string().uuid(),
})
export type RecordOrderChargeBody = z.infer<typeof RecordOrderChargeBodySchema>

// GET /orders/:orderId/charges — every additional charge on an order, in
// insertion order. One order can accrue several.
export const ListOrderChargesResponseSchema = z.object({
  items: z.array(OrderChargeSchema),
})
export type ListOrderChargesResponse = z.infer<typeof ListOrderChargesResponseSchema>

export const OrderRescheduleReasonSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  orderId: z.string().uuid(),
  previousDeliveryDate: z.string().date(),
  newDeliveryDate: z.string().date(),
  previousDeliverySlotId: z.nativeEnum(DeliverySlotWindow).nullable(),
  newDeliverySlotId: z.nativeEnum(DeliverySlotWindow).nullable(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  rescheduledByUserId: z.string().uuid().nullable(),
  reasonType: z.nativeEnum(OrderRescheduleReasonType),
  reasonRefId: z.number().int().nullable(),
  reasonName: z.string().nullable(),
  createdTimeUTC: z.string().datetime(),
  updatedTimeUTC: z.string().datetime(),
})
export type OrderRescheduleReasonDto = z.infer<typeof OrderRescheduleReasonSchema>

export const RecordOrderRescheduleReasonBodySchema = z.object({
  userId: z.string().uuid(),
  orderId: z.string().uuid(),
  previousDeliveryDate: z.string().date(),
  newDeliveryDate: z.string().date(),
  previousDeliverySlotId: z.nativeEnum(DeliverySlotWindow).optional(),
  newDeliverySlotId: z.nativeEnum(DeliverySlotWindow).optional(),
  paymentStatus: z.nativeEnum(PaymentStatus),
  rescheduledByUserId: z.string().uuid().optional(),
  reasonType: z.nativeEnum(OrderRescheduleReasonType),
  reasonRefId: z.number().int().optional(),
  reasonName: z.string().optional(),
  bulkOperationalReschedule: z.boolean().optional(),
})
export type RecordOrderRescheduleReasonBody = z.infer<typeof RecordOrderRescheduleReasonBodySchema>

export const QueryOrderRescheduleReasonsQuerySchema = z.object({
  previousDeliveryDate: z.string().date().optional(),
  reasonType: z.nativeEnum(OrderRescheduleReasonType).optional(),
  reasonRefId: z.coerce.number().int().optional().describe('Operational block id (operational_date_blocks.id) that caused the shift'),
  brand: z.nativeEnum(Brand).optional().describe('Filter by the brand of the associated order (joins to orders table)'),
  userId: z.string().uuid().optional(),
  // Normalise the forms a query string yields for a repeated param before validation:
  // a single value ("uuid"), repeated keys (["uuid", ...]), or a bracket-notation object
  // ({ "0": "uuid" }). Callers must send repeated keys (userIds=a&userIds=b) — the deployed
  // query parser drops bracketed keys, so bracket notation never reaches this preprocess.
  userIds: z
    .preprocess((val) => {
      if (typeof val === 'string')
        return [val]
      if (Array.isArray(val))
        return val
      if (val && typeof val === 'object')
        return Object.values(val)
      return val
    }, z.array(z.string().uuid()))
    .optional()
    .describe('When set with reasonRefId, only return records for these users'),
  limit: z.coerce.number().int().positive().max(500).optional(),
  offset: z.coerce.number().int().nonnegative().optional().default(0),
})

export const ListOrderRescheduleReasonsResponseSchema = z.object({
  items: z.array(OrderRescheduleReasonSchema),
  total: z.number().int().nonnegative().optional().describe('Total matching rows (set for paginated reasonRefId queries)'),
})
export type ListOrderRescheduleReasonsResponse = z.infer<typeof ListOrderRescheduleReasonsResponseSchema>

export type DeleteMigratedOrderDiscountsResponse = z.infer<typeof DeleteMigratedOrderDiscountsResponseSchema>

// ─── NetSuite outbox `order_paid` payload ────────────────────────────────
//
// Two live versions. v3 adds the pricing snapshot below; v2 is every row
// written before it and stays parseable, because PENDING v2 rows can still be
// in flight at deploy time and a replay re-dispatches old rows indefinitely.
// The snapshot fields are optional rather than split across a discriminated
// union so a consumer that ignores them reads both versions through one type.
//
// The snapshot is the order as it stood when the row was written. Restating a
// row for a later adjustment moves only `totalPrice`/`totalCharge`, so on a
// restated row the plan bucket and the line totals no longer add up to the
// order totals, and the gap is the adjustment. Nothing attributes that money to
// a bucket or a line at the moment it is recorded, so there is no honest figure
// to shift them by. Read the snapshot as the order one adjustment ago, never as
// a breakdown of the totals beside it.

const OrderPaidBundleChildSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid().nullable(),
  animal_id: z.string().uuid().nullable(),
  extra_id: z.string().uuid().nullable(),
  quantity: z.number().int(),
  price: z.number().int(),
})

const OrderPaidLineItemSchema = z.object({
  id: z.string().uuid(),
  product_id: z.string().uuid().nullable(),
  product_type: z.nativeEnum(ProductType).nullable(),
  price: z.number().int(),
  quantity: z.number().int(),
  one_off: z.boolean(),
  plan_type: z.nativeEnum(FeedingPlanType).nullable(),
  animal_id: z.string().uuid().nullable(),
  bundle_id: z.string().uuid().nullable(),
  discounted_price: z.number().int().nullable(),
  discount_id: z.string().uuid().nullable(),
  bundle_children: z.array(OrderPaidBundleChildSchema),
  /**
   * v3 pricing snapshot, copied from the persisted per-line pricing columns.
   * Absent on a v2 row; null on a v3 row for an order priced before pricing
   * persistence went live.
   *
   * `total_price` and `total_charge` are line totals, not unit amounts:
   * `discounted_quantity` means only part of a line can be discounted, so unit
   * arithmetic times quantity drifts by cents against what the customer paid.
   */
  total_price: z.number().int().nullable().optional(),
  total_charge: z.number().int().nullable().optional(),
  applied_discount_id: z.string().uuid().nullable().optional(),
  discounted_quantity: z.number().int().nullable().optional(),
})
export type OrderPaidLineItem = z.infer<typeof OrderPaidLineItemSchema>

export const OrderPaidOutboxPayloadSchema = z.object({
  payload_version: z.union([z.literal(2), z.literal(3)]),
  brand: z.nativeEnum(Brand),
  orderNumber: z.number().int(),
  totalPrice: z.number().int(),
  totalCharge: z.number().int(),
  /**
   * v3 plan bucket, copied from the order's persisted plan totals. Lyka meal
   * money lives here and nowhere in the line block, because Lyka meal SKUs are
   * $0 and the real price is volumetric. Same nullability rule as the per-line
   * snapshot: absent on v2, null on a v3 order priced before persistence.
   */
  planTotalPrice: z.number().int().nullable().optional(),
  planTotalCharge: z.number().int().nullable().optional(),
  makeDate: z.string().datetime(),
  deliveryDate: z.string().datetime(),
  /**
   * Timestamp when the order was charged (`order.billedDate`). Set in the same
   * TypeORM save that flips `paymentStatus → PROCESSED`, so it's always present
   * when the outbox subscriber fires. Used by the NetSuite resolver as the SO
   * `tranDate` — matches V1's "first Stripe transaction date" semantics, which
   * lands the SO in the correct accounting period (the charge month, not the
   * warehouse-fulfilment month).
   */
  billedDate: z.string().datetime(),
  depotCode: BeCoolDepotCode,
  facilityId: z.number().int(),
  numberOfBoxes: z.number().int().nonnegative(),
  line_items: z.array(OrderPaidLineItemSchema),
})
export type OrderPaidOutboxPayload = z.infer<typeof OrderPaidOutboxPayloadSchema>

// ─── NetSuite outbox `order.palletised` payload ──────────────────────────
//
// Producer payload for an `order.palletised` row, written when an order's
// fulfilment_status transitions to PALLETISED (OPS-2907). The Item Fulfilment
// consumer reads the Sales Order's internal id and line items from this row's
// SO predecessor (predecessor_id), so the payload only carries what the SO
// snapshot lacks:
//   - `numberOfBoxes` — what actually shipped, snapshotted at palletise time.
//     The SO snapshot captured boxes at payment time; the IF records what left
//     the warehouse, so it carries its own count.
//   - `palletisedAt`  — the fulfilment moment, captured on the row for audit
//     and future enrichment. Kept explicit (not derived from `created_at`) so
//     a replay that mints a fresh row keeps the original palletise moment
//     rather than the replay time. NB: the IF's NetSuite `tranDate` is NOT
//     read from here — it's the SO predecessor payload's `makeDate`, matching
//     V1/Laravel parity (`SalesOrderItemFulfillmentsFactory` uses
//     `$order->make_date`). See `buildItemFulfilmentBody.ts`.
//
// `payload_version` is a literal so a future v2 can reintroduce a discriminated
// union without a wire-format break, matching OrderPaidOutboxPayloadSchema.
export const ItemFulfilmentOutboxPayloadSchema = z.object({
  payload_version: z.literal(1),
  numberOfBoxes: z.number().int().nonnegative(),
  palletisedAt: z.string().datetime(),
})
export type ItemFulfilmentOutboxPayload = z.infer<typeof ItemFulfilmentOutboxPayloadSchema>

// Payload for an `order.delivered:invoice` outbox row (OPS-2909). The Sales
// Invoice consumer reads the NetSuite Sales Order id forward from its IF
// predecessor (predecessor_id → the `order.palletised` row), so this payload
// only carries what the predecessor handoff lacks:
//   - `deliveryDate` — the NetSuite invoice `tranDate` source. V1/Laravel
//     parity: `SalesOrderInvoiceFactory` uses `delivery_date` as the invoice
//     date, so the invoice lands in the period the delivery happened.
//   - `deliveredAt`  — the moment the order transitioned to DELIVERED, captured
//     for audit. Kept explicit (not derived from `created_at`) so a replay that
//     mints a fresh row keeps the original delivery moment. NB: not the invoice
//     `tranDate` — that is `deliveryDate`, matching V1.
//
// `payload_version` is a literal so a future v2 can reintroduce a discriminated
// union without a wire-format break, matching the SO/IF payload schemas.
export const SalesInvoiceOutboxPayloadSchema = z.object({
  payload_version: z.literal(1),
  deliveryDate: z.string().datetime(),
  deliveredAt: z.string().datetime(),
})
export type SalesInvoiceOutboxPayload = z.infer<typeof SalesInvoiceOutboxPayloadSchema>

// ─── NetSuite outbox `order.refunded:*` payload ──────────────────────────
//
// Written by the refund producer when a refund completes, full or partial.
// A full refund mirrors the whole document, so a multi-payment cancellation
// still produces a single row, keyed by the refund that triggered it.
// Partials produce one row each: several can exist for the same order, and
// each credits only its own lines. The money itself stays in
// `payment.refunds`; the payload carries only what the dispatcher can't
// re-derive at claim time.
export const RefundOutboxPayloadSchema = z.object({
  // Two live versions, and the version is what makes a partial row unreadable
  // to a dispatcher that predates the partial branches. Such a dispatcher's
  // schema knows only v1 and, like every plain z.object, drops keys it has no
  // field for — so it would parse a partial row into a full refund and reverse
  // the whole document. Stamping partials v2 turns that into a failed parse:
  // the row is failed closed and replayed once the dispatcher can post it.
  // Full refunds stay v1, which the two dispatchers read identically.
  payload_version: z.union([z.literal(1), z.literal(2)]),
  // payment.refunds id of the refund this reversal is for.
  refundId: z.string().uuid(),
  // When Phoenix recorded the refund (order_refund.created_at).
  refundedAt: z.string().datetime(),
  // True when the order had not been palletised when the refund landed —
  // no itemFulfillment mapping and no live order.palletised outbox row.
  // Drives the Sales Order close (only never-shipped orders are closed).
  // Captured at produce time because it's a point-in-time fact about
  // the refund, not the row.
  preFulfilment: z.boolean(),
  // What the refund credited, copied from the order_refund row so the
  // dispatcher reads the breakdown as it stood when the refund was recorded.
  // Their amounts sum to the money refunded, which is also the reversal's
  // total. `null` or absent means a full refund (mirror the whole document);
  // absent covers rows written before partial refunds were produced at all.
  creditedLines: z.array(CreditedLineSchema).min(1).nullable().optional(),
  // The whole order as a Sales Order payload, snapshotted when the refund was
  // recorded, for restating a partial's Sales Order down to the amount the
  // customer is left paying. Without that restatement the Sales Order keeps its
  // original total, so the invoice bills it and the credited slice becomes open
  // AR.
  //
  // Partials only, and only pre-fulfilment ones: a full refund closes the Sales
  // Order instead, and past fulfilment NetSuite refuses a line replace outright.
  // Absent when the order can't produce a snapshot, and on every row written
  // before this was produced at all — the dispatcher then posts the refund and
  // leaves the Sales Order alone, which is the behaviour those rows already had.
  salesOrder: OrderPaidOutboxPayloadSchema.optional(),
})
export type RefundOutboxPayload = z.infer<typeof RefundOutboxPayloadSchema>

// ─── NetSuite outbox `order.charged:deposit` payload ─────────────────────
//
// Written when an extra payment is taken on an already-paid order. The
// dispatcher posts a second Customer Deposit for that payment, then amends the
// Sales Order to the order's new lines.
//
// The money itself stays in `payment.payments` and is fetched by `paymentId`
// when the deposit posts, so the row and the amount can never drift.
export const ChargeOutboxPayloadSchema = z.object({
  payload_version: z.literal(1),
  // payment.payments id of the extra payment. Keys the deposit's externalId
  // and is how the dispatcher looks the amount up.
  paymentId: z.string().uuid(),
  // When Phoenix recorded the charge (order_charge.created_at).
  chargedAt: z.string().datetime(),
  // True when the order had not been palletised as the charge landed — no
  // itemFulfillment mapping and no live order.palletised outbox row. Past that
  // point NetSuite refuses the amendment, because a whole-sublist replace
  // deletes every line first, so the producer parks the row instead of
  // queueing it. Captured at produce time: it's a fact about the charge, not
  // about the row.
  preFulfilment: z.boolean(),
  // The whole order restated as a Sales Order payload, snapshotted when the
  // charge was recorded. The amendment PATCHes the entire line sublist, so
  // there is no such thing as a partial one: Lyka's discounts are spread
  // across the order rather than sitting on single lines, which makes a
  // line-level diff meaningless. Snapshotting at emit time rather than at
  // dispatch time is what stops a later edit rewriting what an older row meant
  // to say.
  salesOrder: OrderPaidOutboxPayloadSchema,
})
export type ChargeOutboxPayload = z.infer<typeof ChargeOutboxPayloadSchema>

// ─── NetSuite outbox result_payload shapes ───────────────────────────────
//
// `result_payload` is the consumer's write-back column on an outbox row. The
// dispatcher writes it on the SUCCEEDED transition; downstream rows in the
// same DAG (e.g. the deposit row that follows the SO row) read it via
// `predecessor_id` to pick up the NetSuite ids the previous step earned.
//
// Each event_type has its own shape. They share a base — `{ netsuite_entity_id,
// source }` — because every NetSuite POST funnels through the same
// 204 / 400-duplicate / GET-by-eid pattern. Per-resource extensions live on
// the resource's own schema so loadOutboxPredecessor can fail-fast (non-
// retryable) if the predecessor is missing fields the next step needs.

const NetsuiteResultPayloadBaseSchema = z.object({
  netsuite_entity_id: z.string(),
  source: z.enum(['created', 'alreadyExists']),
})

/**
 * Result payload for an `order.paid` row after the Sales Order activity has
 * run. Required fields are read by the downstream `order.paid:deposit`
 * consumer:
 *   - `netsuite_entity_id` — the SO internalId, sent as the deposit's
 *     `salesOrder` body reference.
 *   - `netsuite_customer_id` — the customer internalId resolved by the SO
 *     workflow, sent as the deposit's `customer` body reference. Captured
 *     here so the deposit branch doesn't have to re-run upsertNetsuiteCustomer.
 *
 * `netsuite_so_total` is optional — once the SO activity captures the post-
 * rounding inc-GST total NetSuite computed, the deposit consumer can read it
 * directly instead of falling back to `payload.totalCharge`. See OPS-2905
 * § 7 risk #2 for the drift mitigation.
 */
export const SalesOrderResultPayloadSchema = NetsuiteResultPayloadBaseSchema.extend({
  netsuite_customer_id: z.string(),
  netsuite_so_total: z.number().int().nonnegative().optional(),
})
export type SalesOrderResultPayload = z.infer<typeof SalesOrderResultPayloadSchema>

/**
 * Result payload for an `order.paid:deposit` row after the Customer Deposit
 * branch has run. Two shapes:
 *
 *   - posted — the base `{ netsuite_entity_id, source }`: the deposit exists
 *     in NetSuite. Named export so writers that always post (the deposit
 *     activity) keep the narrow contract.
 *   - skipped — `{ source: 'skippedZeroAmount' }`: the order's charge is $0,
 *     so the dispatcher deliberately posted nothing. NetSuite refuses
 *     zero-amount payment records ("Value 0.0 outside of valid min/max range
 *     for field payment"), and the free-order ledger convention (carried over
 *     from Laravel) is SO + IF + $0 invoice with no money documents. The row
 *     still SUCCEEDs; this shape is the audit record of why there is no
 *     NetSuite id.
 */
export const CustomerDepositPostedResultPayloadSchema = NetsuiteResultPayloadBaseSchema
export type CustomerDepositPostedResultPayload = z.infer<typeof CustomerDepositPostedResultPayloadSchema>

export const CustomerDepositSkippedResultPayloadSchema = z.object({
  source: z.literal('skippedZeroAmount'),
})
export type CustomerDepositSkippedResultPayload = z.infer<typeof CustomerDepositSkippedResultPayloadSchema>

export const CustomerDepositResultPayloadSchema = z.discriminatedUnion('source', [
  CustomerDepositPostedResultPayloadSchema,
  CustomerDepositSkippedResultPayloadSchema,
])
export type CustomerDepositResultPayload = z.infer<typeof CustomerDepositResultPayloadSchema>

/**
 * Result payload for an `order.palletised` row after the Item Fulfilment
 * activity has run. Read by the downstream `order.delivered:invoice` consumer
 * (predecessor = IF), so it carries the Sales Order id forward:
 *   - `netsuite_entity_id` — the IF internalId (audit / replay).
 *   - `netsuite_sales_order_id` — the SO internalId the IF was transformed
 *     from. The invoice transforms the same SO (`salesOrder/<id>/!transform/
 *     invoice`), so it reads the id from here rather than re-walking to the SO
 *     row. Required: a stale IF row written before OPS-2909 lacks it, and the
 *     invoice consumer must fail closed (non-retryable) on those rather than
 *     transform from an undefined Sales Order — see OPS-2909 § risks #2.
 */
export const ItemFulfilmentResultPayloadSchema = NetsuiteResultPayloadBaseSchema.extend({
  netsuite_sales_order_id: z.string(),
})
export type ItemFulfilmentResultPayload = z.infer<typeof ItemFulfilmentResultPayloadSchema>

/**
 * Result payload for an `order.delivered:invoice` row after the Sales Invoice
 * activity has run. Just the base shape — the invoice is the current leaf of
 * the V1 chain (no Deposit Application consumer reads it yet). Written on the
 * SUCCEEDED transition to keep the row's audit trail symmetric with the SO/
 * CD/IF rows and give a future Deposit Application step a stable hook.
 */
export const SalesInvoiceResultPayloadSchema = NetsuiteResultPayloadBaseSchema
export type SalesInvoiceResultPayload = z.infer<typeof SalesInvoiceResultPayloadSchema>

/**
 * Result payload for an `order.charged:deposit` row after the additional-charge
 * branch has run.
 *
 * Two shapes, split on `source` the way the original deposit's is:
 *
 *   - posted — `netsuite_entity_id` is the extra Customer Deposit's internalId.
 *   - skipped — `source: 'skippedZeroAmount'`, no deposit id, because NetSuite
 *     refuses zero-amount payment records. The Sales Order is still amended.
 *
 * A SUCCEEDED row normally amended the Sales Order as well, and a failure in that
 * half fails the whole row. The one exception is a row that was overtaken: when a
 * later charge on the same order has already restated the Sales Order, this row
 * leaves it alone and says so with `amendment_skipped_superseded`. Amendment
 * bodies snapshot the whole order, so the later row has already stated everything
 * this one would, and re-sending an older snapshot would undo it.
 *
 * Both carry `netsuite_sales_order_id` and `netsuite_customer_id` forward. An
 * order can be charged more than once, and each charge row chains behind the
 * previous one, so the next row reads the two ids from here rather than walking
 * back down the chain to the `order.paid` row — the same forward carry the Item
 * Fulfilment row does for the invoice.
 */
const ChargeDepositResultPayloadBaseSchema = z.object({
  netsuite_sales_order_id: z.string(),
  netsuite_customer_id: z.string(),
  /**
   * Set only when this row left the Sales Order untouched because a later charge
   * had already restated it. Absent means this row amended it.
   */
  amendment_skipped_superseded: z.literal(true).optional(),
})

export const ChargeDepositPostedResultPayloadSchema = ChargeDepositResultPayloadBaseSchema.extend({
  netsuite_entity_id: z.string(),
  source: z.enum(['created', 'alreadyExists']),
})
export type ChargeDepositPostedResultPayload = z.infer<typeof ChargeDepositPostedResultPayloadSchema>

export const ChargeDepositSkippedResultPayloadSchema = ChargeDepositResultPayloadBaseSchema.extend({
  source: z.literal('skippedZeroAmount'),
})
export type ChargeDepositSkippedResultPayload = z.infer<typeof ChargeDepositSkippedResultPayloadSchema>

export const ChargeDepositResultPayloadSchema = z.discriminatedUnion('source', [
  ChargeDepositPostedResultPayloadSchema,
  ChargeDepositSkippedResultPayloadSchema,
])
export type ChargeDepositResultPayload = z.infer<typeof ChargeDepositResultPayloadSchema>

/**
 * Result payload for an `order.refunded:customerRefund` row after the
 * Customer Refund activity has run. `netsuite_entity_id` is the Customer
 * Refund internalId.
 *
 * The two Sales Order flags say what the dispatcher did to it, and they are
 * mutually exclusive: a full refund before fulfilment closes it, a partial
 * before fulfilment restates it down to the reduced total. Both are for the
 * audit trail — no downstream row reads either.
 */
export const CustomerRefundResultPayloadSchema = NetsuiteResultPayloadBaseSchema.extend({
  netsuite_sales_order_closed: z.boolean().optional(),
  netsuite_sales_order_amended: z.boolean().optional(),
})
export type CustomerRefundResultPayload = z.infer<typeof CustomerRefundResultPayloadSchema>

export const OrderComplaintProductSchema = z.object({
  id: z.string().uuid(),
  orderComplaintId: z.string().uuid(),
  productVariantId: z.string().uuid(),
  quantity: z.number().int().nonnegative(),
  productionDate: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export const MigrateOrderComplaintProductInputSchema = OrderComplaintProductSchema.omit({ orderComplaintId: true })

export const OrderComplaintInputSchema = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid(),
  responsibility: OrderComplaintResponsibilitySchema.nullable().optional(),
  complaintDate: z.coerce.date(),
  additionalDetails: z.string().optional().nullable(),
  customerCareResolution: z.string().optional().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().optional().nullable(),
  complaintProducts: z.array(MigrateOrderComplaintProductInputSchema),
})
export type OrderComplaintInputDto = z.infer<typeof OrderComplaintInputSchema>

export const OrderComplaintSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid(),
  orderNumber: z.number().int(),
  userId: z.string().uuid(),
  categoryId: z.string().uuid(),
  categoryName: z.string(),
  subCategoryName: z.string().optional(),
  responsibility: OrderComplaintResponsibilitySchema.nullable().optional(),
  complaintDate: z.coerce.date(),
  additionalDetails: z.string().optional().nullable(),
  customerCareResolution: z.string().optional().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().optional().nullable(),
  complaintProducts: z.array(MigrateOrderComplaintProductInputSchema),
  totalUnknownMeals: z.number().int().nonnegative().optional().nullable(),
  totalUnknownTreats: z.number().int().nonnegative().optional().nullable(),
  totalUnknownSupplements: z.number().int().nonnegative().optional().nullable(),
  productsUnknown: z.boolean().optional().default(false),
})
export type OrderComplaintDto = z.infer<typeof OrderComplaintSchema>

export type OrderComplaintProductDto = z.infer<typeof OrderComplaintProductSchema>

export const MigrateOrderComplaintSchema = z.array(OrderComplaintInputSchema)

export type MigrateOrderComplaintPayloadDto = z.infer<typeof MigrateOrderComplaintSchema>

export const CreateOrderComplaintProductInputSchema = z.object({
  productVariantId: z.string().uuid(),
  quantity: z.number().int().nonnegative(),
  productionDate: z.coerce.date().optional().nullable(),
})
export type CreateOrderComplaintProductInputDto = z.infer<typeof CreateOrderComplaintProductInputSchema>

export const CreateOrderComplaintInputSchema = z.object({
  categoryId: z.string().uuid(),
  responsibility: OrderComplaintResponsibilitySchema.nullable().optional(),
  complaintDate: z.coerce.date(),
  additionalDetails: z.string().optional().nullable(),
  customerCareResolution: z.string().optional().nullable(),
  createdBy: z.string().uuid(),
  complaintProducts: z.array(CreateOrderComplaintProductInputSchema).optional().default([]),
  totalUnknownMeals: z.number().int().nonnegative().optional().nullable(),
  totalUnknownTreats: z.number().int().nonnegative().optional().nullable(),
  totalUnknownSupplements: z.number().int().nonnegative().optional().nullable(),
  productsUnknown: z.boolean().optional().default(false),
})
export type CreateOrderComplaintInputDto = z.infer<typeof CreateOrderComplaintInputSchema>

export const UpdateOrderComplaintInputSchema = CreateOrderComplaintInputSchema.omit({ createdBy: true }).partial()
export type UpdateOrderComplaintInputDto = z.infer<typeof UpdateOrderComplaintInputSchema>

export const OrderComplaintCategorySchema = z.object({
  id: z.string().uuid(),
  parentId: z.string().uuid().nullable(),
  slug: z.string(),
  name: z.string(),
  responsibility: OrderComplaintResponsibilitySchema.nullable(),
  requireProductionDate: z.boolean(),
  responsibilitySelectable: z.boolean(),
  displayOrder: z.number().int().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})
export type OrderComplaintCategoryDto = z.infer<typeof OrderComplaintCategorySchema>

// Categories are only ever 2 levels deep (category -> subcategory), so the tree is flattened one level rather than recursive.
export const OrderComplaintCategoryTreeSchema = OrderComplaintCategorySchema.extend({
  subcategories: z.array(OrderComplaintCategorySchema),
})
export type OrderComplaintCategoryTreeDto = z.infer<typeof OrderComplaintCategoryTreeSchema>

export const GetComplaintCategoriesResponseSchema = z.array(OrderComplaintCategoryTreeSchema)

export const GetOrderComplaintsQuerySchema = z
  .object({
    userId: z.string().uuid().optional().describe('Return complaints for every order belonging to this user'),
    orderId: z.string().uuid().optional().describe('Return complaints for this order'),
    offset: z.coerce.number().int().nonnegative().optional().default(0),
    limit: z.coerce.number().int().positive().max(500).optional().default(200),
  })
  .refine(query => query.userId !== undefined || query.orderId !== undefined, {
    message: 'At least one of userId or orderId must be provided',
  })
export type GetOrderComplaintsQueryDto = z.infer<typeof GetOrderComplaintsQuerySchema>

export const GetOrderComplaintsResponseSchema = generateResultsWithOptionalPagination(OrderComplaintSchema)
export type GetOrderComplaintsResponseDto = z.infer<typeof GetOrderComplaintsResponseSchema>

export const DeletePendingOrderByUserIdResponseSchema = z.object({ affected: z.number() })
export type DeletePendingOrderByUserIdDto = z.infer<typeof DeletePendingOrderByUserIdResponseSchema>

export const SearchOrdersQuerySchema = z.object({
  searchText: z.string().min(1),
  limit: z.coerce.number().int().min(1).max(50).optional().default(25),
  offset: z.coerce.number().int().min(0).optional().default(0),
}).superRefine((data, ctx) => {
  if (!isValidSearchOrdersQueryText(data.searchText)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'searchText must be a full UUID or digits',
      path: ['searchText'],
    })
  }
})

export const SearchOrderResultSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.number().int(),
  userId: z.string().uuid(),
  brand: z.nativeEnum(Brand),
  deliveryDate: z.string().date(),
  expectedDeliverySlotId: z.string().nullable(),
  fulfilmentStatus: z.nativeEnum(JobStatus),
})

export const SearchOrdersResponseSchema = z.object({
  data: z.array(SearchOrderResultSchema),
  meta: z.object({
    offset: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
  }),
})

export type SearchOrdersQuery = z.infer<typeof SearchOrdersQuerySchema>
export type SearchOrderResult = z.infer<typeof SearchOrderResultSchema>
export type SearchOrdersResponse = z.infer<typeof SearchOrdersResponseSchema>
